import React, { Fragment, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, Button, ButtonGroup, FormHelp, Heading, Icon, IconButton, Image, Input, Label, LabeledInput, Modal, Paragraph, Popover, Spinner, Text, TextButton, Toast, ToastProvider, useToast } from "@zillow/constellation-10";
import { nanoid } from "nanoid";
import { IconCheckmarkCircleFilled, IconCheckmarkFilled, IconCloseFilled, IconDeleteOutline, IconFileOutline, IconFilePDFOutline, IconPlusFilled, IconPlusOutline, IconQuestionMarkCircleOutline, IconReloadFilled, IconWarningFilled, IconZoomInOutline } from "@zillow/constellation-icons";
import { useDropzone } from "react-dropzone";
var FieldPermission = /* @__PURE__ */ function(FieldPermission) {
	FieldPermission["ADD"] = "ADD";
	FieldPermission["REMOVE"] = "REMOVE";
	FieldPermission["POSITION"] = "POSITION";
	FieldPermission["ASSIGNMENT"] = "ASSIGNMENT";
	FieldPermission["LABEL"] = "LABEL";
	FieldPermission["APPEARANCE"] = "APPEARANCE";
	FieldPermission["HELP"] = "HELP";
	FieldPermission["CONDITION"] = "CONDITION";
	FieldPermission["VALUE"] = "VALUE";
	FieldPermission["MATERIAL"] = "MATERIAL";
	FieldPermission["LINKED_FIELDS"] = "LINKED_FIELDS";
	return FieldPermission;
}({});
var FieldType = {
	Signature: "SIGNATURE",
	Initial: "INITIAL",
	Text: "TEXT",
	Date: "DATE",
	Time: "TIME",
	DateTime: "DATETIME",
	Radio: "RADIO",
	Checkbox: "CHECKBOX",
	Dropdown: "DROPDOWN",
	Strike: "STRIKE",
	Note: "NOTE",
	Image: "IMAGE"
};
var ReadinessInfoState = /* @__PURE__ */ function(ReadinessInfoState) {
	ReadinessInfoState["InProgress"] = "IN_PROGRESS";
	ReadinessInfoState["Ready"] = "READY";
	ReadinessInfoState["Failed"] = "FAILED";
	return ReadinessInfoState;
}({});
//#endregion
//#region ../core/lib/iframe-events.ts
var IFrameEventName = /* @__PURE__ */ function(IFrameEventName) {
	IFrameEventName["Error"] = "error";
	IFrameEventName["ReviewComplete"] = "reviewComplete";
	IFrameEventName["DocumentSaved"] = "documentSaved";
	IFrameEventName["DocumentSaveSummary"] = "documentSavedSummary";
	IFrameEventName["DocumentRevisionRefresh"] = "documentRevisionRefresh";
	IFrameEventName["FieldClicked"] = "fieldClicked";
	IFrameEventName["FieldPlaced"] = "fieldPlaced";
	IFrameEventName["FieldRemoved"] = "fieldRemoved";
	IFrameEventName["ModalOpened"] = "modalOpened";
	IFrameEventName["ModalClosed"] = "modalClosed";
	IFrameEventName["HasChanges"] = "hasChanges";
	IFrameEventName["HasNoChanges"] = "hasNoChanges";
	IFrameEventName["MlDataRequest"] = "mlDataRequest";
	IFrameEventName["SubmitFeedbackDataRequest"] = "submitFeedbackDataRequest";
	IFrameEventName["UserActivity"] = "userActivity";
	IFrameEventName["TokenExpired"] = "tokenExpired";
	IFrameEventName["ProgressSaved"] = "progressSaved";
	IFrameEventName["ActiveDocumentChanged"] = "activeDocumentChanged";
	IFrameEventName["DownloadClicked"] = "downloadClicked";
	IFrameEventName["ShareClicked"] = "shareClicked";
	IFrameEventName["PrintClicked"] = "printClicked";
	IFrameEventName["DocumentsLoaded"] = "documentsLoaded";
	IFrameEventName["GuidedSigningChanged"] = "guidedSigningChanged";
	IFrameEventName["SelectedFieldChanged"] = "selectedFieldChanged";
	IFrameEventName["ActionCompleted"] = "actionCompleted";
	return IFrameEventName;
}({});
var IFrameModalEventName = /* @__PURE__ */ function(IFrameModalEventName) {
	IFrameModalEventName["SignatureModal"] = "signature";
	return IFrameModalEventName;
}({});
var IFrameErrorTypes = /* @__PURE__ */ function(IFrameErrorTypes) {
	IFrameErrorTypes["MissingToken"] = "missingToken";
	IFrameErrorTypes["InvalidToken"] = "invalidToken";
	IFrameErrorTypes["ExpiredToken"] = "expiredToken";
	IFrameErrorTypes["FetchError"] = "fetchError";
	IFrameErrorTypes["SaveError"] = "saveError";
	IFrameErrorTypes["PageImageError"] = "pageImageError";
	IFrameErrorTypes["Unknown"] = "unknownError";
	return IFrameErrorTypes;
}({});
//#endregion
//#region ../core/hooks/useCallbackRef.ts
function useCallbackRef(initialValue) {
	const [ref, setRef] = useState(initialValue);
	return [ref, useCallback((node) => {
		if (node !== null) setRef(node);
	}, [])];
}
//#endregion
//#region src/editor/ZDocsEditor.tsx
var TEN_SECONDS = 1e4;
var ONE_MINUTE = TEN_SECONDS * 10;
var MESSAGE_EVENT = "message";
var EDITOR_VERSION = "469bef48beb344ce31fcd0c8b627ad7927db444b";
var ZDocsEditorIFrame = forwardRef(({ css, ...props }, ref) => /* @__PURE__ */ React.createElement(Box, {
	asChild: true,
	css: {
		width: "100%",
		height: "100%",
		border: 0,
		...css
	}
}, /* @__PURE__ */ React.createElement("iframe", {
	...props,
	ref
})));
ZDocsEditorIFrame.displayName = "ZDocsEditorIFrame";
function postIFrameMessage(iFrame, targetOrigin, event, meta) {
	iFrame?.contentWindow?.postMessage({
		event,
		meta
	}, targetOrigin);
}
function handlePendingActionResult({ resolve, reject }, error) {
	if (error) {
		reject({ reason: error });
		return;
	}
	resolve({});
}
var ZDocsEditor = forwardRef(function ZDocsEditor({ url, id, className = "", css, title, name, onLoad, onError, onEditorError, onFieldPlaced, onFieldClicked, onFieldRemoved, onModalVisibilityChange, onReviewCompleted, onGuidedSigningChanged, onSelectedFieldChanged, onHasChanges, onCanUndoChanges, onCanRedoChanges, onTokenExpired, onDocumentSaved, onSaveFinished, onProgressSaveFinished, onDocumentRevisionRefresh, onActiveDocumentChanged, onUserActivity, onDownloadClicked, onShareClicked, onPrintClicked, onDocumentsLoaded }, ref) {
	const [iFrame, setIFrame] = useCallbackRef(null);
	const pendingActionsRef = useRef(/* @__PURE__ */ new Map());
	const sentEditorVersionRef = useRef(false);
	const { origin: targetOrigin } = new URL(url);
	const resolveAction = useCallback((messageId, resolveWith) => {
		if (!messageId) return;
		const { current: pendingActions } = pendingActionsRef;
		const pendingAction = pendingActions.get(messageId);
		if (!pendingAction) return;
		clearTimeout(pendingAction.timeoutId);
		pendingActions.delete(messageId);
		resolveWith(pendingAction);
	}, []);
	const requestAction = useCallback((message, timeoutMs) => new Promise((resolve, reject) => {
		const messageId = nanoid();
		const { current: pendingActions } = pendingActionsRef;
		pendingActions.set(messageId, {
			timeoutId: setTimeout(() => resolveAction(messageId, (action) => handlePendingActionResult(action, "timeout")), timeoutMs),
			resolve,
			reject
		});
		postIFrameMessage(iFrame, targetOrigin, message.event, {
			...message.meta,
			messageId
		});
	}), [
		iFrame,
		targetOrigin,
		resolveAction
	]);
	const trySendEditorVersion = useCallback(() => {
		if (!sentEditorVersionRef.current) {
			postIFrameMessage(iFrame, targetOrigin, "editorVersion", { version: EDITOR_VERSION });
			sentEditorVersionRef.current = true;
		}
	}, [iFrame, targetOrigin]);
	const handleModalVisibilityEvent = useCallback((eventData) => {
		const { event, meta } = eventData;
		const { name } = meta;
		onModalVisibilityChange?.(name, event === IFrameEventName.ModalOpened);
	}, [onModalVisibilityChange]);
	const handleHasChangesEvent = useCallback((eventData) => {
		const { event, meta } = eventData;
		const hasChangesMeta = meta;
		onHasChanges?.(event === IFrameEventName.HasChanges);
		if (hasChangesMeta) {
			onCanUndoChanges?.(hasChangesMeta.canUndo);
			onCanRedoChanges?.(hasChangesMeta.canRedo);
		}
	}, [
		onHasChanges,
		onCanUndoChanges,
		onCanRedoChanges
	]);
	const handleDocumentSaveSummaryEvent = useCallback((eventMetaData) => {
		const { success, summary, messageId } = eventMetaData;
		onSaveFinished?.(success, summary);
		resolveAction(messageId, ({ resolve, reject }) => success ? resolve(summary) : reject(summary));
	}, [onSaveFinished, resolveAction]);
	const handleActionCompletedEvent = useCallback(({ messageId, error }) => resolveAction(messageId, (action) => handlePendingActionResult(action, error)), [resolveAction]);
	const handlePostMessage = useCallback((iFrameMessageEvent) => {
		const { data } = iFrameMessageEvent;
		const { event, meta } = data;
		if (!event) return;
		trySendEditorVersion();
		switch (event) {
			case IFrameEventName.Error:
				onEditorError?.(meta);
				break;
			case IFrameEventName.FieldPlaced:
				onFieldPlaced?.(meta);
				break;
			case IFrameEventName.FieldClicked:
				onFieldClicked?.(meta);
				break;
			case IFrameEventName.FieldRemoved:
				onFieldRemoved?.(meta);
				break;
			case IFrameEventName.ModalOpened:
			case IFrameEventName.ModalClosed:
				handleModalVisibilityEvent(data);
				break;
			case IFrameEventName.ReviewComplete:
				onReviewCompleted?.();
				break;
			case IFrameEventName.SelectedFieldChanged:
				onSelectedFieldChanged?.(meta);
				return;
			case IFrameEventName.GuidedSigningChanged:
				onGuidedSigningChanged?.(meta);
				break;
			case IFrameEventName.ActionCompleted:
				handleActionCompletedEvent(meta);
				break;
			case IFrameEventName.HasChanges:
			case IFrameEventName.HasNoChanges:
				handleHasChangesEvent(data);
				break;
			case IFrameEventName.TokenExpired:
				onTokenExpired?.();
				break;
			case IFrameEventName.DocumentSaved: {
				const { documentId } = meta;
				onDocumentSaved?.(documentId);
				break;
			}
			case IFrameEventName.DocumentSaveSummary:
				handleDocumentSaveSummaryEvent(meta);
				break;
			case IFrameEventName.DocumentRevisionRefresh:
				onDocumentRevisionRefresh?.();
				break;
			case IFrameEventName.UserActivity:
				onUserActivity?.();
				break;
			case IFrameEventName.ProgressSaved:
				onProgressSaveFinished?.();
				break;
			case IFrameEventName.DownloadClicked: {
				const { fileUrl } = meta;
				onDownloadClicked?.(fileUrl);
				break;
			}
			case IFrameEventName.ShareClicked: {
				const { fileUrl } = meta;
				onShareClicked?.(fileUrl);
				break;
			}
			case IFrameEventName.PrintClicked: {
				const { fileUrl } = meta;
				onPrintClicked?.(fileUrl);
				break;
			}
			case IFrameEventName.ActiveDocumentChanged: {
				const { documentId } = meta;
				onActiveDocumentChanged?.(documentId);
				break;
			}
			case IFrameEventName.DocumentsLoaded:
				onDocumentsLoaded?.(meta);
				break;
			case IFrameEventName.MlDataRequest:
			case IFrameEventName.SubmitFeedbackDataRequest: break;
			default:
		}
	}, [
		trySendEditorVersion,
		onEditorError,
		onFieldPlaced,
		onFieldClicked,
		onFieldRemoved,
		handleModalVisibilityEvent,
		onReviewCompleted,
		onGuidedSigningChanged,
		onSelectedFieldChanged,
		handleHasChangesEvent,
		onTokenExpired,
		onDocumentSaved,
		handleDocumentSaveSummaryEvent,
		handleActionCompletedEvent,
		onProgressSaveFinished,
		onDocumentRevisionRefresh,
		onActiveDocumentChanged,
		onUserActivity,
		onDownloadClicked,
		onShareClicked,
		onPrintClicked,
		onDocumentsLoaded
	]);
	useEffect(() => {
		window.addEventListener(MESSAGE_EVENT, handlePostMessage);
		return () => window.removeEventListener(MESSAGE_EVENT, handlePostMessage);
	}, [handlePostMessage]);
	useImperativeHandle(ref, () => ({
		reload: () => {
			if (iFrame) {
				const reloadUrl = new URL(iFrame.src);
				reloadUrl.searchParams.set("_reload", Date.now().toString());
				iFrame.src = reloadUrl.toString();
			}
		},
		undoChanges: () => postIFrameMessage(iFrame, targetOrigin, "undoDocumentChanges"),
		redoChanges: () => postIFrameMessage(iFrame, targetOrigin, "redoDocumentChanges"),
		save: () => requestAction({ event: "saveDocument" }, ONE_MINUTE),
		saveProgress: () => postIFrameMessage(iFrame, targetOrigin, "saveProgress"),
		selectField: ({ fieldId, action }) => requestAction({
			event: "selectField",
			meta: {
				fieldId,
				action
			}
		}, TEN_SECONDS)
	}));
	return /* @__PURE__ */ React.createElement(ZDocsEditorIFrame, {
		id,
		className,
		css,
		ref: setIFrame,
		src: url,
		title,
		name,
		onLoad,
		onError
	});
});
//#endregion
//#region src/organize-pages/utils/page-range-utils.ts
var pageRangesRegex = /^[0-9]+(?:(?:\s*,\s*|-)[0-9]+)*$/;
function buildPageRange(pages, documentId) {
	let pageList = "";
	const selectedPages = pages.filter((page) => page.selected && page.documentId === documentId);
	selectedPages.sort((a, b) => a.selectedOrder > b.selectedOrder ? 1 : -1);
	let i = 0;
	for (i = 0; i < selectedPages.length; i++) if (i + 1 < selectedPages.length) {
		pageList = pageList.concat(selectedPages[i].pageNumber.toString());
		let y;
		const startPageRange = selectedPages[i].pageNumber;
		let pageRangeSize = 1;
		for (y = i + 1; y < selectedPages.length; y++) if (selectedPages[y].pageNumber === startPageRange + pageRangeSize) pageRangeSize++;
		else y = selectedPages.length;
		if (pageRangeSize > 1) {
			pageList = pageList.concat("-" + (selectedPages[i].pageNumber + (pageRangeSize - 1)).toString());
			i = i + (pageRangeSize - 1);
			if (i < selectedPages.length - 1) pageList = pageList.concat(",");
		} else pageList = pageList.concat(",");
	} else pageList = pageList.concat(selectedPages[i].pageNumber.toString());
	return pageList;
}
function isSelectedPage(selectedPages, documentId, pageNumber) {
	return selectedPages.find((documentPage) => documentPage.documentId === documentId && documentPage.pageNumber === pageNumber && documentPage.selected) !== void 0;
}
function hasValidPageRanges(composeDoc) {
	for (let i = 0; i < composeDoc.sourceDocuments.length; i++) {
		const sourceDoc = composeDoc.sourceDocuments[i];
		if (!isValidPageRangesFormat(sourceDoc.selectedPages) || !isValidPageRangesText(sourceDoc.selectedPages, sourceDoc.sourcePages.length)) return false;
	}
	return true;
}
function isValidPageRange(pageRangesText, totalPages) {
	return isValidPageRangesFormat(pageRangesText) && isValidPageRangesText(pageRangesText, totalPages);
}
function isValidPageRangesFormat(pageRangesText) {
	return pageRangesRegex.test(pageRangesText);
}
function isValidPageRangesText(pageRangesText, endPageNumber) {
	const selectedPageRangesArray = pageRangesText.split(",");
	let i;
	for (i = 0; i < selectedPageRangesArray.length; i++) {
		const pageRange = getPageRange(selectedPageRangesArray[i]);
		if (pageRange.endPage) {
			if (pageRange.startPage > pageRange.endPage || pageRange.startPage > endPageNumber || pageRange.endPage > endPageNumber || pageRange.startPage === pageRange.endPage) return false;
		} else if (pageRange.startPage > endPageNumber) return false;
	}
	return true;
}
function getPageRange(pageRangesText) {
	const dashIndex = pageRangesText.indexOf("-");
	if (dashIndex > -1) {
		const pageRangeText = pageRangesText;
		return {
			startPage: parseInt(pageRangeText.substring(0, dashIndex), 10),
			endPage: parseInt(pageRangeText.substring(dashIndex + 1), 10)
		};
	} else return { startPage: parseInt(pageRangesText, 10) };
}
function getRotationDetails(sourcePages, documentId, pageRange) {
	const rotationDetails = [];
	if (pageRange.endPage) for (let k = pageRange.startPage; k < pageRange.endPage; k++) {
		const sourcePage = sourcePages.find((page) => page.documentId === documentId && page.pageNumber === k);
		if (sourcePage && sourcePage.rotation > 0) rotationDetails.push({
			startPage: sourcePage.pageNumber,
			endPage: sourcePage.pageNumber,
			rotationDegrees: sourcePage.rotation
		});
	}
	else {
		const sourcePage = sourcePages.find((page) => page.documentId === documentId && page.pageNumber === pageRange.startPage);
		if (sourcePage && sourcePage.rotation > 0) rotationDetails.push({
			startPage: sourcePage.pageNumber,
			endPage: sourcePage.pageNumber,
			rotationDegrees: sourcePage.rotation
		});
	}
	return rotationDetails;
}
//#endregion
//#region src/organize-pages/utils/compose-documents-utils.ts
function getActiveSourceDocument(composeDocumentList, activeComposeId) {
	const activeComposeDoc = composeDocumentList.find((compDoc) => compDoc.id === activeComposeId);
	if (activeComposeDoc) return activeComposeDoc.sourceDocuments.find((sourceDoc) => sourceDoc.isActive);
}
function getActiveSourceDocumentIndex(composeDocument) {
	if (composeDocument) return composeDocument.sourceDocuments.findIndex((sourceDoc) => sourceDoc.isActive);
	return 0;
}
function calculateSelectOrder(sourcePages) {
	let nextSelectedOrder = sourcePages.reduce((a, b) => a.selectedOrder > b.selectedOrder ? a : b).selectedOrder;
	if (!nextSelectedOrder || nextSelectedOrder === 0) nextSelectedOrder = 1;
	else nextSelectedOrder++;
	return nextSelectedOrder;
}
function isValidDocumentName(composeDoc) {
	return composeDoc.name.trim().length > 0;
}
function buildSourceDocument(document, isActiveDocument, sourcePages) {
	return {
		documentId: document.id,
		name: document.name,
		sourcePages: (sourcePages ?? document.pages).map((page) => ({
			id: page.id,
			documentId: page.documentId,
			pageNumber: page.pageNumber,
			height: page.height,
			width: page.width,
			scaleFactor: page.scaleFactor,
			pageImage: page.pageImage,
			selected: false,
			selectedOrder: 0,
			rotation: "rotation" in page ? page.rotation : 0
		})),
		selectedPages: "",
		isActive: isActiveDocument
	};
}
function updateDeselectedPages(activeSourceDoc, deselectedPage) {
	const updatedPages = [];
	if (activeSourceDoc.sourcePages.find((page) => page.selected && page.documentId === deselectedPage.documentId && page.pageNumber === deselectedPage.pageNumber)) updatedPages.push({
		...deselectedPage,
		selected: false,
		selectedOrder: 0
	});
	return updatedPages;
}
//#endregion
//#region src/organize-pages/ComposeDocumentItem.tsx
var NewDocumentRow = ({ isActive, children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-around",
		borderLeft: isActive ? "0.25rem solid {colors.brand}" : "0.25rem solid transparent",
		width: "100%",
		backgroundColor: isActive ? "{colors.blue.200}" : "transparent"
	},
	...props
}, children);
var FormErrorSpacer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	asChild: true,
	css: { height: "1.25rem" },
	...props
}, /* @__PURE__ */ React.createElement("span", null, children));
var ComposeDocRowCentered = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center"
	},
	...props
}, children);
var InputWithErrorContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "column",
		alignItems: "start",
		justifyContent: "space-between",
		paddingTop: "1.25rem"
	},
	...props
}, children);
var ComposeDocumentContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "column",
		alignItems: "start",
		background: "transparent",
		"&:focus": { outline: "none" }
	},
	...props
}, children);
function ComposeDocumentItem({ isActive, composeDocument, order, hasError, onNameChange, onComposeDocumentSelection, onPageRangesChange, onDeleteComposeDocument }) {
	const handleDocumentNameChange = (e) => {
		onNameChange(e.target.value);
	};
	const handlePageRangeChange = (e) => {
		onPageRangesChange(e.target.value);
	};
	const hasInvalidName = hasError && !isValidDocumentName(composeDocument);
	const hasInvalidPageRange = hasError && !isValidPageRange(composeDocument.sourceDocuments[getActiveSourceDocumentIndex(composeDocument)].selectedPages, composeDocument.sourceDocuments[getActiveSourceDocumentIndex(composeDocument)].sourcePages.length);
	return /* @__PURE__ */ React.createElement(ComposeDocumentContainer, { key: composeDocument.id }, /* @__PURE__ */ React.createElement(NewDocumentRow, { isActive }, /* @__PURE__ */ React.createElement(ComposeDocRowCentered, null, /* @__PURE__ */ React.createElement(Paragraph, { textStyle: "body-lg-bold" }, order)), /* @__PURE__ */ React.createElement(InputWithErrorContainer, null, /* @__PURE__ */ React.createElement(LabeledInput, {
		label: /* @__PURE__ */ React.createElement(Label, null, "Document Name"),
		input: /* @__PURE__ */ React.createElement(Input, {
			"aria-label": `Document Name ${order}`,
			value: composeDocument.name,
			onFocus: () => onComposeDocumentSelection(composeDocument.id),
			onChange: handleDocumentNameChange,
			error: hasInvalidName
		})
	}), hasInvalidName && /* @__PURE__ */ React.createElement(FormHelp, { error: true }, "Please include a document name."), !hasInvalidName && /* @__PURE__ */ React.createElement(FormErrorSpacer, null)), /* @__PURE__ */ React.createElement(InputWithErrorContainer, null, /* @__PURE__ */ React.createElement(LabeledInput, {
		label: /* @__PURE__ */ React.createElement(Label, null, "Select Page Ranges"),
		input: /* @__PURE__ */ React.createElement(Input, {
			"aria-label": `Select Page Ranges Document ${order}`,
			placeholder: "Eg: 1-3,5,6-8",
			value: composeDocument.sourceDocuments[getActiveSourceDocumentIndex(composeDocument)].selectedPages,
			onFocus: () => onComposeDocumentSelection(composeDocument.id),
			onChange: handlePageRangeChange,
			error: hasInvalidPageRange
		})
	}), hasInvalidPageRange && /* @__PURE__ */ React.createElement(FormHelp, { error: true }, "Please enter a valid page number."), !hasInvalidPageRange && /* @__PURE__ */ React.createElement(FormErrorSpacer, null)), /* @__PURE__ */ React.createElement(ComposeDocRowCentered, null, /* @__PURE__ */ React.createElement(IconButton, {
		title: `Delete Document ${order}`,
		onClick: () => onDeleteComposeDocument(composeDocument.id),
		emphasis: "bare",
		size: "sm",
		icon: /* @__PURE__ */ React.createElement(IconCloseFilled, null)
	}))));
}
//#endregion
//#region src/organize-pages/ComposeDocumentsPanel.tsx
var NewDocumentsPanelContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "column",
		width: "35%",
		height: "100%"
	},
	...props
}, children);
var SectionHeading = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Text, {
	...props,
	css: { padding: "0.75rem" }
}, children);
var SectionText$1 = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Text, {
	css: { padding: "0.75rem" },
	...props
}, children);
var SummaryRow = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	...props,
	css: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		paddingBottom: "0.75rem"
	}
}, children);
var ComposeDocumentListContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	...props,
	css: {
		overflowY: "auto",
		overflowX: "hidden"
	}
}, children);
var AddDocumentContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	...props,
	css: {
		paddingTop: "1rem",
		paddingLeft: "0.5rem",
		paddingBottom: "1rem"
	}
}, children);
function ComposeDocumentsPanel({ activeComposeId, composeDocumentList, pages, onAddComposeDocument, onComposeDocumentSelection, onPageRangesChange, onNameChange, onDeleteComposeDocument, hasError }) {
	return /* @__PURE__ */ React.createElement(NewDocumentsPanelContainer, null, /* @__PURE__ */ React.createElement(SectionHeading, { textStyle: "body-sm-bold" }, "Split Summary"), /* @__PURE__ */ React.createElement(SummaryRow, null, /* @__PURE__ */ React.createElement(SectionText$1, { textStyle: "body-sm" }, "To get started, select the pages that you would like to extract into a newly created copy of the original. Create additional documents as needed.")), /* @__PURE__ */ React.createElement(ComposeDocumentListContainer, null, composeDocumentList.map((composeDocument, index) => /* @__PURE__ */ React.createElement(ComposeDocumentItem, {
		key: composeDocument.id,
		isActive: activeComposeId === composeDocument.id,
		composeDocument,
		pages,
		order: index + 1,
		hasError,
		onAddComposeDocument,
		onComposeDocumentSelection,
		onPageRangesChange,
		onNameChange,
		onDeleteComposeDocument
	})), /* @__PURE__ */ React.createElement(AddDocumentContainer, null, /* @__PURE__ */ React.createElement(Button, {
		"aria-label": "Add new document",
		onClick: onAddComposeDocument
	}, "Add new document"))));
}
//#endregion
//#region ../core/lib/utils/browser.ts
var windowShim = typeof window !== "undefined" ? window : void 0;
//#endregion
//#region ../core/lib/utils/dom-util.ts
function convertRemToPx(remValue) {
	const documentStyle = windowShim?.getComputedStyle(document.documentElement);
	return (typeof remValue === "string" ? parseFloat(remValue) : remValue) * (documentStyle?.fontSize ? parseInt(documentStyle.fontSize, 10) : 16);
}
//#endregion
//#region src/organize-pages/ZoomPageModal.tsx
var PageImage$1 = ({ src, pageWidth, alt }) => /* @__PURE__ */ React.createElement(Image, {
	src,
	alt,
	style: { width: `${pageWidth}px` }
});
function ZoomPageModal({ page, ...rest }) {
	let pageImageUrl = page.pageImage.url;
	if (page.rotation > 0) pageImageUrl += `&rotation=${page.rotation}`;
	return /* @__PURE__ */ React.createElement(Modal, {
		...rest,
		size: "fullScreen",
		header: /* @__PURE__ */ React.createElement(Heading, { level: 6 }, "Page ", page.pageNumber),
		body: /* @__PURE__ */ React.createElement(PageImage$1, {
			src: pageImageUrl,
			pageWidth: page.width,
			alt: `Page ${page.pageNumber}`
		})
	});
}
//#endregion
//#region src/organize-pages/DocumentThumbnailHorizontalViewer.tsx
var THUMBNAIL_WIDTH = convertRemToPx("13rem");
var PagesContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	...props,
	css: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
		width: "100%",
		gridGap: "1rem",
		background: "transparent"
	}
}, children);
var ThumbnailContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "start",
		paddingLeft: "1rem",
		paddingRight: "1rem",
		alignItems: "center",
		border: "none",
		background: "transparent"
	},
	...props
}, children);
var PageButton = ({ isLoaded, children, ...props }) => /* @__PURE__ */ React.createElement(Button, {
	...props,
	emphasis: "outlined",
	style: { opacity: isLoaded ? 1 : 0 },
	css: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		border: "none",
		background: "transparent",
		"&:focus": { outline: "none" }
	}
}, children);
var PageImage = ({ isActive, pageHeight, rotation, src, alt, onLoad }) => /* @__PURE__ */ React.createElement(Box, {
	asChild: true,
	style: {
		border: `2px solid ${isActive ? "{colors.blue.500}" : "none"}`,
		boxShadow: isActive ? "none" : "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
		width: THUMBNAIL_WIDTH,
		height: `${pageHeight}px`,
		transform: `rotate(${rotation}deg)`
	}
}, /* @__PURE__ */ React.createElement("img", {
	src,
	alt,
	onLoad
}));
var PageControlSection = ({ isLoaded, children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	asChild: true,
	style: { opacity: isLoaded ? 1 : 0 },
	css: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-around",
		alignItems: "center",
		width: "100%",
		border: "none",
		background: "transparent",
		paddingTop: "0.5rem",
		paddingBottom: "2rem",
		"&:focus": { outline: "none" }
	},
	...props
}, /* @__PURE__ */ React.createElement("section", null, children));
function Thumbnail({ documentName, page, selectedPage, onPageSelected, onZoom, onPageRotation }) {
	const pageNumber = page.pageImage.pageNumber.toString().padStart(2, "0");
	const [isLoaded, setIsLoaded] = useState(false);
	const handlePageSelected = function(e, page) {
		onPageSelected(page, e.shiftKey);
	};
	const pageHeight = THUMBNAIL_WIDTH * (page.height / page.width);
	const isActive = selectedPage;
	const url = `${`${page.pageImage.url}`}&width=${THUMBNAIL_WIDTH}`;
	const label = `${documentName} page ${pageNumber}`;
	return /* @__PURE__ */ React.createElement(ThumbnailContainer, null, /* @__PURE__ */ React.createElement(PageButton, {
		"aria-selected": isActive,
		"aria-label": label,
		key: page.id,
		isLoaded,
		onClick: (e) => handlePageSelected(e, page)
	}, /* @__PURE__ */ React.createElement(PageImage, {
		isActive: selectedPage,
		src: url,
		alt: `Thumbnail for ${label}`,
		onLoad: () => setIsLoaded(true),
		pageHeight,
		rotation: page.rotation
	})), /* @__PURE__ */ React.createElement(PageControlSection, { isLoaded }, /* @__PURE__ */ React.createElement(IconButton, {
		title: `Rotate Page ${pageNumber}`,
		onClick: () => onPageRotation(page),
		size: "sm",
		emphasis: "bare",
		icon: /* @__PURE__ */ React.createElement(IconReloadFilled, null)
	}), /* @__PURE__ */ React.createElement(TextButton, {
		tone: "neutral",
		onClick: (e) => handlePageSelected(e, page)
	}, pageNumber), /* @__PURE__ */ React.createElement(IconButton, {
		title: `Zoom Page ${pageNumber}`,
		onClick: () => onZoom(page),
		size: "sm",
		emphasis: "bare",
		icon: /* @__PURE__ */ React.createElement(IconZoomInOutline, null)
	})));
}
function DocumentThumbnailHorizontalViewer({ documentId, documentName, pages, onPageSelected, onPageRotation }) {
	const [pageToZoom, setPageToZoom] = useState(null);
	const closeZoom = function() {
		setPageToZoom(null);
	};
	return /* @__PURE__ */ React.createElement(PagesContainer, null, pages.filter((page) => page.documentId === documentId).map((p) => /* @__PURE__ */ React.createElement(Thumbnail, {
		key: p.id,
		documentName,
		page: p,
		selectedPage: isSelectedPage(pages, p.documentId, p.pageNumber),
		onPageSelected,
		onZoom: setPageToZoom,
		onPageRotation
	})), pageToZoom && /* @__PURE__ */ React.createElement(ZoomPageModal, {
		open: true,
		onClose: closeZoom,
		page: pageToZoom
	}));
}
var DocumentThumbnailHorizontalViewer_default = memo(DocumentThumbnailHorizontalViewer);
//#endregion
//#region src/organize-pages/PageSelectionPanel.tsx
var PagesPanelContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		overflowY: "auto",
		height: "100%",
		flexDirection: "column",
		width: "65%"
	},
	...props
}, children);
var SectionText = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Text, {
	css: {
		display: "block",
		paddingTop: "0.75rem",
		paddingBottom: "0.75rem"
	},
	...props
}, children);
var DocumentThumbnailViewerContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		overflowY: "auto",
		display: "flex",
		justifyContent: "space-between",
		flexWrap: "wrap",
		padding: "1rem",
		backgroundColor: "{colors.gray.200}"
	},
	...props
}, children);
var PageSelectionPanel = function({ documentId, documentName, sourcePages, onPageSelected, onPageRotation, onSelectAllPages }) {
	return /* @__PURE__ */ React.createElement(PagesPanelContainer, null, /* @__PURE__ */ React.createElement(SectionText, { textStyle: "body-sm" }, "Use Shift to select continuous pages or", " ", /* @__PURE__ */ React.createElement(TextButton, { onClick: onSelectAllPages }, "Select All")), /* @__PURE__ */ React.createElement(DocumentThumbnailViewerContainer, null, /* @__PURE__ */ React.createElement(DocumentThumbnailHorizontalViewer_default, {
		documentId,
		documentName,
		pages: sourcePages,
		onPageSelected,
		onPageRotation
	})));
};
//#endregion
//#region src/organize-pages/OrganizePagesHeader.tsx
var OrganizePagesContainer = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		width: "100%",
		background: "white",
		display: "flex",
		padding: "0.75rem 1.5rem",
		border: "solid 1px {colors.gray.300}",
		"& > *": { margin: "auto" }
	},
	...props
}, children);
var HeaderDetails = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		flexDirection: "column"
	},
	...props
}, children);
var CollectionItemName = ({ children, ...props }) => /* @__PURE__ */ React.createElement(Heading, {
	css: {
		marginBottom: "0.25rem",
		color: "{colors.gray.600}"
	},
	...props
}, children);
var HeaderSpacer = ({ ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		flexGrow: 2,
		marginLeft: "0.5rem"
	},
	...props
});
var OrganizePagesHeader = function({ documentName, onSave, onCancel }) {
	return /* @__PURE__ */ React.createElement(OrganizePagesContainer, null, /* @__PURE__ */ React.createElement(HeaderDetails, null, /* @__PURE__ */ React.createElement(CollectionItemName, { level: 6 }, "Split Document: ", documentName)), /* @__PURE__ */ React.createElement(HeaderSpacer, null), /* @__PURE__ */ React.createElement(ButtonGroup, { "aria-label": "Organize Pages actions" }, /* @__PURE__ */ React.createElement(Button, {
		css: { marginRight: "0.375rem" },
		onClick: onCancel
	}, "Cancel"), /* @__PURE__ */ React.createElement(Button, {
		css: { marginRight: "0.375rem" },
		emphasis: "filled",
		onClick: onSave
	}, "Save")));
};
//#endregion
//#region ../core/lib/network/errors.ts
var NetworkError = class NetworkError extends Error {
	errors;
	body;
	headers;
	response;
	constructor(errors, response) {
		super("NetworkError");
		Object.setPrototypeOf(this, NetworkError.prototype);
		this.errors = errors;
		this.response = response;
		this.headers = response?.headers;
	}
};
var BadRequestError = class BadRequestError extends NetworkError {
	constructor(errors, body, response) {
		super(errors, response);
		Object.setPrototypeOf(this, BadRequestError.prototype);
		this.errors = errors;
		this.body = body;
	}
};
var UnauthorizedError = class UnauthorizedError extends NetworkError {
	constructor(errors, response) {
		super(errors, response);
		Object.setPrototypeOf(this, UnauthorizedError.prototype);
		this.errors = errors;
	}
};
var ForbiddenError = class ForbiddenError extends NetworkError {
	constructor(errors, response) {
		super(errors, response);
		Object.setPrototypeOf(this, ForbiddenError.prototype);
		this.errors = errors;
	}
};
var VersionConflictError = class VersionConflictError extends NetworkError {
	constructor(errors, body, response) {
		super(errors, response);
		Object.setPrototypeOf(this, VersionConflictError.prototype);
		this.errors = errors;
		this.body = body;
	}
};
var InternalServerError = class InternalServerError extends NetworkError {
	constructor(errors, response) {
		super(errors, response);
		Object.setPrototypeOf(this, InternalServerError.prototype);
		this.errors = errors;
	}
};
//#endregion
//#region ../core/lib/network/network.ts
async function handleResponseError(response) {
	if (!response.ok) {
		let error;
		let jsonBody;
		try {
			jsonBody = await response.json();
		} catch (_) {}
		switch (response.status) {
			case 400:
				error = new BadRequestError(void 0, jsonBody, response);
				break;
			case 401:
				error = new UnauthorizedError(void 0, response);
				break;
			case 403:
				error = new ForbiddenError(void 0, response);
				break;
			case 409:
				error = new VersionConflictError(void 0, jsonBody, response);
				break;
			case 500:
				error = new InternalServerError(void 0, response);
				break;
			default: error = new NetworkError(void 0, response);
		}
		throw error;
	}
}
async function handleResponse(response) {
	await handleResponseError(response);
	const headers = response.headers;
	let json;
	try {
		json = response.status === 204 ? null : await response.json();
	} catch (e) {}
	return {
		headers,
		json,
		response
	};
}
async function makeRequest(input, token, overrideInit, abortController) {
	const options = {
		...overrideInit,
		headers: {
			...overrideInit?.headers ?? {},
			Accept: "application/json",
			Authorization: `Bearer ${token}`,
			["Content-Type"]: "application/json"
		}
	};
	return handleResponse(await fetch(encodeURI(input), {
		...options,
		signal: abortController?.signal
	}));
}
function postInit(body) {
	return {
		method: "POST",
		body: body ? JSON.stringify(body) : void 0
	};
}
async function jsonPost(url, token, body, headers) {
	return makeRequest(url, token, {
		...postInit(body),
		headers
	});
}
//#endregion
//#region src/organize-pages/lib/api/index.ts
var api_default = { composeDocuments: async (token, composeDocuments) => {
	try {
		return await jsonPost("/api/v1/documents/actions/composeAll", token, composeDocuments);
	} catch (e) {
		const json = {
			results: [],
			errors: []
		};
		if (e instanceof NetworkError) {
			if (e.body) json.errors = e.body.errors ?? [];
			return {
				json,
				headers: e.headers,
				response: e.response
			};
		} else return {
			json,
			headers: void 0,
			response: void 0
		};
	}
} };
//#endregion
//#region ../core/lib/logging/logger-shim.ts
var logger = {
	debug: function(message, messageContext) {
		console.debug({
			message,
			messageContext
		});
	},
	info: function(message, messageContext) {
		console.info({
			message,
			messageContext
		});
	},
	warn: function(message, messageContext) {
		console.warn({
			message,
			messageContext
		});
	},
	error: function(message, messageContext) {
		console.error({
			message,
			messageContext
		});
	}
};
//#endregion
//#region ../core/lib/utils/backoff.ts
function backoff(method, maxTime = 1e4, initialBackoff = 500) {
	const startTime = Date.now();
	let attempts = 0;
	return new Promise(async (resolve, reject) => {
		const makeAttempt = async () => {
			const response = await method();
			if (response !== false) return resolve(response);
			if (Date.now() - startTime > maxTime) {
				const errorMessage = "Time expired while attempting to find response";
				logger.warn(errorMessage);
				return reject(errorMessage);
			}
			const delay = Math.pow(attempts++, 2) * initialBackoff;
			setTimeout(makeAttempt, delay);
		};
		try {
			await makeAttempt();
		} catch (e) {
			reject(e);
		}
	});
}
//#endregion
//#region ../core/lib/api/normalize-documents-utils.ts
function normalizePageImage(relationshipData, includedDataMap) {
	const { id, attributes: { url, pageNumber, expiresAt } } = includedDataMap.documentPageImages[relationshipData.id];
	return {
		id,
		url,
		pageNumber,
		expiresAt
	};
}
function normalizeDocumentPages(relationshipData, includedDataMap) {
	return relationshipData.reduce((pages, pageRelationship) => {
		const pageResource = includedDataMap.documentPages[pageRelationship.id];
		if (!pageResource) logger.error(`Page resource not found! Page "${pageRelationship.id}"`);
		else {
			const { id, attributes: { documentId, pageNumber, width, height, scaleFactor, hasContent, hasLinkContent, hasFieldContent }, relationships: { pageImage } } = pageResource;
			const page = {
				id,
				documentId,
				pageNumber,
				width,
				height,
				scaleFactor,
				hasContent,
				hasLinkContent,
				hasFieldContent,
				pageImage: normalizePageImage(pageImage.data, includedDataMap)
			};
			pages.push(page);
		}
		return pages;
	}, []).sort((page1, page2) => page1.pageNumber - page2.pageNumber);
}
function normalizeProcessingStatus(relationshipData, includedDataMap) {
	const { id, attributes: { inProgress } } = includedDataMap.processingStatus[relationshipData.id];
	return {
		id,
		inProgress
	};
}
var basicFieldPermissions = [
	FieldPermission.ADD,
	FieldPermission.REMOVE,
	FieldPermission.POSITION
];
var basicEditableFieldPermissions = basicFieldPermissions.concat(FieldPermission.VALUE);
var allFieldPermissions = Object.values(FieldPermission);
var nonLinkableFieldPermissions = allFieldPermissions.filter((p) => p !== FieldPermission.LINKED_FIELDS);
var allFieldTypes = Object.values(FieldType);
function normalizeFullAccessForFieldType(fieldType) {
	switch (fieldType) {
		case FieldType.Signature:
		case FieldType.Initial: return allFieldPermissions;
		case FieldType.Note: return basicEditableFieldPermissions;
		case FieldType.Image:
		case FieldType.Strike: return basicFieldPermissions;
	}
	return nonLinkableFieldPermissions;
}
function normalizeEffectiveFieldPermissionsForFieldType(fieldType, fieldPermission) {
	if (fieldType === FieldType.Note && fieldPermission === FieldPermission.VALUE) return FieldPermission.HELP;
	return fieldPermission;
}
function normalizeFieldPermissions(fieldType, fieldPermissions) {
	const effectivePermissions = fieldPermissions.includes("ALL") ? normalizeFullAccessForFieldType(fieldType) : fieldPermissions;
	const resultPermissions = allFieldPermissions.reduce((result, fieldPermission) => {
		result[fieldPermission] = false;
		return result;
	}, {});
	return effectivePermissions.reduce((result, fieldPermission) => {
		const effectivePermission = normalizeEffectiveFieldPermissionsForFieldType(fieldType, fieldPermission);
		result[effectivePermission] = true;
		return result;
	}, resultPermissions);
}
function normalizeFieldRuleTypeForFieldType(fieldType) {
	if (fieldType === FieldType.DateTime || fieldType === FieldType.Time) return FieldType.Date;
	return fieldType;
}
function normalizeFieldRules(fields) {
	const defaultFieldPermissions = fields["DEFAULT"] ?? [];
	return allFieldTypes.reduce((fieldRules, fieldType) => {
		const fieldPermissions = fields[normalizeFieldRuleTypeForFieldType(fieldType)];
		fieldRules[fieldType] = fieldPermissions ? normalizeFieldPermissions(fieldType, fieldPermissions) : normalizeFieldPermissions(fieldType, defaultFieldPermissions);
		return fieldRules;
	}, {});
}
function normalizeAccessRules(accessRules) {
	const { fields } = accessRules;
	return { fields: normalizeFieldRules(fields) };
}
function normalizeDocumentAcl(relationshipData, includedDataMap) {
	const { id, attributes: { accessRules } } = includedDataMap.documentAcls[relationshipData.id];
	return {
		id,
		accessRules: accessRules ? normalizeAccessRules(accessRules) : void 0
	};
}
function normalizeDocuments(payload, normalizeLatestRevision, normalizeLatestProgress) {
	const { data, included } = payload;
	const includedDataMap = included?.reduce((dataMap, resource) => {
		const { id, type } = resource;
		if (!dataMap[type]) dataMap[type] = {};
		dataMap[type][id] = resource;
		return dataMap;
	}, {}) ?? {};
	return data.map((document) => {
		const { id, attributes, relationships: { pages, latestRevision, latestProgress, processingStatus, documentAcl } } = document;
		return {
			id,
			...attributes,
			latestRevision: !!latestRevision && !!normalizeLatestRevision ? normalizeLatestRevision(latestRevision.data, includedDataMap) : void 0,
			latestProgress: !!latestProgress && !!normalizeLatestProgress ? normalizeLatestProgress(latestProgress.data, includedDataMap) : void 0,
			pages: pages ? normalizeDocumentPages(pages.data, includedDataMap) : [],
			processingStatus: processingStatus ? normalizeProcessingStatus(processingStatus.data, includedDataMap) : void 0,
			acl: documentAcl ? normalizeDocumentAcl(documentAcl.data, includedDataMap) : void 0
		};
	});
}
//#endregion
//#region ../core/lib/api/document.ts
var JSON_API_HEADERS = {
	Accept: "application/vnd.api+json",
	"Content-Type": "application/vnd.api+json"
};
var DOCUMENT_DELETED_MESSAGE = "document_deleted";
function documentStatistics(documents) {
	return documents.map((document) => {
		const { id, fileSize, mediaType, pages, readinessInfo, processingStatus } = document;
		return {
			id,
			fileSize,
			mediaType,
			pageCount: pages?.length ?? 0,
			isProcessing: readinessInfo ? readinessInfo.state === ReadinessInfoState.InProgress : processingStatus?.inProgress
		};
	});
}
function fetchDocuments(token, normalizeLatestRevision, normalizeLatestProgress, options) {
	const include = [
		"documentAcl",
		"processingStatus",
		"pages.pageImage"
	];
	if (options?.includeLatestRevisionFieldDataAcl !== false) include.push("latestRevision.fieldDataAcl");
	if (options?.includeLatestProgress === true) include.push("latestProgress");
	return backoff(async () => {
		const result = await fetch(`/api/v1/documents?include=${include.join()}`, { headers: {
			...JSON_API_HEADERS,
			Authorization: `Bearer ${token}`
		} });
		if (result.status === 403) throw Error(DOCUMENT_DELETED_MESSAGE);
		else {
			const documents = normalizeDocuments(await result.json(), !!normalizeLatestRevision ? normalizeLatestRevision : void 0, !!normalizeLatestProgress ? normalizeLatestProgress : void 0);
			return documentStatistics(documents).some((document) => document.isProcessing) ? false : documents;
		}
	}, 5500, 500);
}
var document_default = { fetchDocuments };
//#endregion
//#region ../core/hooks/useFetchDocuments.ts
function useFetchDocuments(token) {
	const [documentList, setDocumentList] = useState([]);
	try {
		useEffect(() => {
			async function getDocumentList() {
				const documents = await document_default.fetchDocuments(token);
				if (documents) setDocumentList(documents);
			}
			getDocumentList();
		}, [token]);
	} catch (e) {
		if (e instanceof Error) logger.error(e.message);
	}
	return documentList;
}
//#endregion
//#region src/organize-pages/LoadingIndicator.tsx
function LoadingIndicator() {
	return /* @__PURE__ */ React.createElement(Box, { css: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "100vh"
	} }, /* @__PURE__ */ React.createElement(Text, null, "Loading..."));
}
//#endregion
//#region src/organize-pages/OrganizePagesComponent.tsx
var __id__ = 0;
function uniqueId() {
	__id__++;
	return `${__id__}`;
}
var SplitViewContainer = (props) => /* @__PURE__ */ React.createElement(Box, {
	...props,
	css: {
		display: "flex",
		height: "calc(100vh - 70px)"
	}
});
var OrganizePagesError = function OrganizePagesError({ errorMessage }) {
	const { enqueueToast } = useToast();
	useEffect(() => {
		if (errorMessage) enqueueToast(/* @__PURE__ */ React.createElement(Toast, { tone: "critical" }, errorMessage));
	}, [errorMessage, enqueueToast]);
	return null;
};
var ZDocsOrganizePages = function({ token, onCancel, onSave, onSaveError }) {
	const [errorMessage, setErrorMessage] = useState("");
	const documentList = useFetchDocuments(token);
	const [activeComposeId, setActiveComposeId] = useState(null);
	const [composeDocumentList, setComposeDocumentList] = useState([]);
	const [lastSelectedPage, setLastSelectedPage] = useState(null);
	useEffect(() => {
		if (documentList && documentList.length > 0) {
			const sourceDocuments = documentList.map((doc) => buildSourceDocument(doc, documentList[0].id === doc.id));
			const id = uniqueId();
			setActiveComposeId(id);
			setComposeDocumentList([{
				id,
				name: "",
				sourceDocuments
			}]);
		}
	}, [documentList]);
	const addNewComposeDocument = function() {
		const sourceDocuments = documentList.map((doc) => buildSourceDocument(doc, documentList[0].id === doc.id));
		const composeDocId = uniqueId();
		const composeDocument = {
			id: composeDocId,
			name: "",
			sourceDocuments
		};
		setErrorMessage("");
		setActiveComposeId(composeDocId);
		setComposeDocumentList((c) => [...c, composeDocument]);
	};
	const deleteComposeDocument = function(composeDocId) {
		if (composeDocumentList.length === 1) setErrorMessage("Must have at least one document to create.");
		else {
			const deleteIndex = composeDocumentList.findIndex((composeDoc) => composeDoc.id === composeDocId);
			const newActiveDocIndex = deleteIndex === 0 ? 1 : deleteIndex - 1;
			const activeDocId = composeDocumentList[newActiveDocIndex].id;
			setActiveComposeId(activeDocId);
			setComposeDocumentList((c) => c.filter((compDoc) => compDoc.id !== composeDocId));
		}
	};
	const changeActiveComposeDocument = function(composeDocumentId) {
		setActiveComposeId(composeDocumentId);
	};
	const handleNameChange = function(documentName) {
		setComposeDocumentList((c) => c.map((compDoc) => ({
			...compDoc,
			name: compDoc.id === activeComposeId ? documentName : compDoc.name
		})));
	};
	const handlePageRotation = function(page) {
		const updatedList = composeDocumentList.map((compDoc) => {
			const sourceDocIndex = compDoc.sourceDocuments.findIndex((sourceDoc) => sourceDoc.documentId === page.documentId);
			if (sourceDocIndex > -1) {
				const sourceDoc = compDoc.sourceDocuments[sourceDocIndex];
				const updatedSourcePages = sourceDoc.sourcePages.map((sourcePage) => {
					if (sourcePage.pageNumber === page.pageNumber) {
						let rotation = 0;
						if (sourcePage.rotation === 270) rotation = 0;
						else rotation = sourcePage.rotation + 90;
						return {
							...sourcePage,
							rotation
						};
					} else return sourcePage;
				});
				const updatedSourceDoc = {
					documentId: sourceDoc.documentId,
					name: sourceDoc.name,
					sourcePages: updatedSourcePages,
					selectedPages: sourceDoc.selectedPages,
					isActive: sourceDoc.isActive
				};
				const sourceDocumentList = compDoc.sourceDocuments.slice();
				sourceDocumentList[sourceDocIndex] = updatedSourceDoc;
				return {
					...compDoc,
					sourceDocuments: sourceDocumentList
				};
			} else return { ...compDoc };
		});
		setComposeDocumentList(updatedList);
	};
	const handlePageSelection = function(page, shift) {
		const activeSourceDoc = getActiveSourceDocument(composeDocumentList, activeComposeId);
		if (!activeSourceDoc) return;
		let updatedPages = [];
		if (isSelectedPage(activeSourceDoc.sourcePages, page.documentId, page.pageNumber)) updatedPages = updateDeselectedPages(activeSourceDoc, page);
		else updatedPages = updateSelectedPages(activeSourceDoc, page, shift);
		updateSelectedPageState(updatedPages);
	};
	const updateSelectedPageState = function(updatedPages) {
		setComposeDocumentList((c) => c.map((compDoc) => {
			if (compDoc.id === activeComposeId) return {
				...compDoc,
				sourceDocuments: compDoc.sourceDocuments.map((sourceDoc) => {
					if (sourceDoc.isActive) {
						const newSourcePages = sourceDoc.sourcePages.map((sourcePage) => {
							const selectedPage = updatedPages.find((selectedPage) => selectedPage.documentId === sourcePage.documentId && selectedPage.pageNumber === sourcePage.pageNumber);
							if (selectedPage) return selectedPage;
							return sourcePage;
						});
						return {
							...sourceDoc,
							sourcePages: newSourcePages,
							selectedPages: buildPageRange(newSourcePages, sourceDoc.documentId)
						};
					}
					return sourceDoc;
				})
			};
			return compDoc;
		}));
	};
	const updateSelectedPages = function(activeSourceDoc, selectedPage, shift) {
		const updatedPages = [];
		let nextSelectedOrder = calculateSelectOrder(activeSourceDoc.sourcePages);
		if (shift && lastSelectedPage) {
			if (lastSelectedPage) {
				const startPage = lastSelectedPage;
				const startPageNum = startPage.pageNumber;
				const endPageNum = selectedPage.pageNumber;
				if (startPage.pageNumber > selectedPage.pageNumber) for (let j = startPageNum; j >= endPageNum; j--) selectSourcePage(j, nextSelectedOrder++, activeSourceDoc, updatedPages);
				else for (let j = startPage.pageNumber; j <= endPageNum; j++) selectSourcePage(j, nextSelectedOrder++, activeSourceDoc, updatedPages);
				setLastSelectedPage(selectedPage);
			}
		} else {
			setLastSelectedPage(selectedPage);
			selectSourcePage(selectedPage.pageNumber, nextSelectedOrder, activeSourceDoc, updatedPages);
		}
		return updatedPages;
	};
	const selectSourcePage = function(pageNumber, nextSelectedOrder, activeSourceDoc, updatedPages) {
		const selectPage = activeSourceDoc.sourcePages.find((sourcePage) => sourcePage.pageNumber === pageNumber && sourcePage.documentId === activeSourceDoc.documentId);
		if (selectPage && !selectPage.selected) updatedPages.push({
			...selectPage,
			selected: true,
			selectedOrder: nextSelectedOrder
		});
	};
	const handleSelectAllPages = function() {
		const activeSourceDoc = getActiveSourceDocument(composeDocumentList, activeComposeId);
		if (!activeSourceDoc) return;
		const startPage = activeSourceDoc.sourcePages[0].pageNumber;
		const endPage = activeSourceDoc.sourcePages[activeSourceDoc.sourcePages.length - 1].pageNumber;
		let nextSelectedOrder = calculateSelectOrder(activeSourceDoc.sourcePages);
		const updatedPages = [];
		for (let j = startPage; j <= endPage; j++) selectSourcePage(j, nextSelectedOrder++, activeSourceDoc, updatedPages);
		updateSelectedPageState(updatedPages);
	};
	const handlePageRangesChange = function(selectedPageRanges) {
		const activeSourceDoc = getActiveSourceDocument(composeDocumentList, activeComposeId);
		if (activeSourceDoc) {
			const sourceDocumentId = activeSourceDoc.documentId;
			const selectedPageRangesArray = selectedPageRanges.split(",");
			const isValid = isValidPageRange(selectedPageRanges, activeSourceDoc.sourcePages.length);
			const selectedDocumentPageNumbers = {};
			let order = 1;
			for (let i = 0; i < selectedPageRangesArray.length; i++) {
				const pageRange = getPageRange(selectedPageRangesArray[i]);
				if (pageRange.endPage) for (let j = pageRange.startPage; j <= pageRange.endPage; j++) selectedDocumentPageNumbers[j] = order++;
				else selectedDocumentPageNumbers[pageRange.startPage] = order++;
			}
			setComposeDocumentList((c) => c.map((compDoc) => {
				if (compDoc.id === activeComposeId) return {
					...compDoc,
					sourceDocuments: compDoc.sourceDocuments.map((s) => {
						if (s.isActive) {
							const newSourcePages = s.sourcePages.map((sourcePage) => ({
								...sourcePage,
								selected: !!selectedDocumentPageNumbers[sourcePage.pageNumber],
								selectedOrder: selectedDocumentPageNumbers[sourcePage.pageNumber] ?? 0
							}));
							return {
								...s,
								sourcePages: newSourcePages,
								selectedPages: isValid ? buildPageRange(newSourcePages, sourceDocumentId) : selectedPageRanges
							};
						}
						return s;
					})
				};
				return compDoc;
			}));
		}
	};
	const validateSave = function() {
		let hasError = false;
		for (let j = 0; j < composeDocumentList.length; j++) if (!isValidDocumentName(composeDocumentList[j]) || !hasValidPageRanges(composeDocumentList[j])) {
			hasError = true;
			break;
		}
		if (hasError) setErrorMessage("Please fix the errors to proceed.");
		return !hasError;
	};
	const handleSave = async function() {
		if (validateSave()) {
			const composeDocuments = [];
			composeDocumentList.forEach((composeDoc) => {
				const sourceDocuments = [];
				for (let j = 0; j < composeDoc.sourceDocuments.length; j++) {
					const { selectedPages, sourcePages, documentId } = composeDoc.sourceDocuments[j];
					if (selectedPages.length > 0) {
						const selectedPageRanges = selectedPages.split(",");
						for (let i = 0; i < selectedPageRanges.length; i++) {
							const pageRange = getPageRange(selectedPageRanges[i]);
							const rotationDetails = getRotationDetails(sourcePages, documentId, pageRange);
							sourceDocuments.push({
								documentId,
								startPage: pageRange.startPage,
								endPage: pageRange.endPage ?? pageRange.startPage,
								rotationDetails
							});
						}
					}
				}
				composeDocuments.push({
					documentName: composeDoc.name,
					sourceDocuments
				});
			});
			try {
				const response = await api_default.composeDocuments(token, composeDocuments);
				if (response.response?.status === 200) onSave(response.json);
				else onSaveError(response.json, response.response?.status);
			} catch (e) {}
		}
	};
	const activeSourceDoc = getActiveSourceDocument(composeDocumentList, activeComposeId);
	if (!documentList || documentList.length === 0 || !activeSourceDoc) return /* @__PURE__ */ React.createElement(LoadingIndicator, null);
	return /* @__PURE__ */ React.createElement(ToastProvider, {
		portal: false,
		placement: "top"
	}, /* @__PURE__ */ React.createElement(OrganizePagesError, { errorMessage }), /* @__PURE__ */ React.createElement(OrganizePagesHeader, {
		documentName: activeSourceDoc.name,
		onSave: handleSave,
		onCancel
	}), /* @__PURE__ */ React.createElement(SplitViewContainer, null, /* @__PURE__ */ React.createElement(ComposeDocumentsPanel, {
		composeDocumentList,
		activeComposeId,
		pages: activeSourceDoc.sourcePages,
		onAddComposeDocument: addNewComposeDocument,
		onComposeDocumentSelection: changeActiveComposeDocument,
		onPageRangesChange: handlePageRangesChange,
		onNameChange: handleNameChange,
		onDeleteComposeDocument: deleteComposeDocument,
		hasError: !!errorMessage
	}), /* @__PURE__ */ React.createElement(PageSelectionPanel, {
		documentId: activeSourceDoc.documentId,
		documentName: activeSourceDoc.name,
		sourcePages: activeSourceDoc.sourcePages,
		onPageSelected: handlePageSelection,
		onPageRotation: handlePageRotation,
		onSelectAllPages: handleSelectAllPages
	})));
};
//#endregion
//#region src/uploader/lib/react-dropzone-utils.ts
function accepts(file, acceptedFiles) {
	if (file && acceptedFiles) {
		const acceptedFilesArray = acceptedFiles.split(",");
		if (acceptedFilesArray.length === 0) return true;
		const fileName = file.name || "";
		const mimeType = (file.type || "").toLowerCase();
		const baseMimeType = mimeType.replace(/\/.*$/, "");
		return acceptedFilesArray.some((type) => {
			const validType = type.trim().toLowerCase();
			if (validType.charAt(0) === ".") return fileName.toLowerCase().endsWith(validType);
			else if (validType.endsWith("/*")) return baseMimeType === validType.replace(/\/.*$/, "");
			return mimeType === validType;
		});
	}
	return true;
}
var FILE_INVALID_TYPE = "file-invalid-type";
var FILE_TOO_LARGE = "file-too-large";
var FILE_TOO_SMALL = "file-too-small";
var TOO_MANY_FILES = "too-many-files";
var TOO_MANY_FILES_REJECTION = {
	code: TOO_MANY_FILES,
	message: "Too many files"
};
function isMIMEType(v) {
	return v === "audio/*" || v === "video/*" || v === "image/*" || v === "text/*" || v === "application/*" || /\w+\/[-+.\w]+/g.test(v);
}
function isExt(v) {
	return /^.*\.[\w]+$/.test(v);
}
function acceptPropAsAcceptAttr(accept) {
	if (accept) return Object.entries(accept).reduce((a, [mimeType, ext]) => [
		...a,
		mimeType,
		...ext
	], []).filter((v) => isMIMEType(v) || isExt(v)).join(",");
	return "";
}
var getInvalidTypeRejectionErr = (accept = "") => {
	const acceptArr = accept.split(",");
	return {
		code: FILE_INVALID_TYPE,
		message: `File type must be ${acceptArr.length > 1 ? `one of ${acceptArr.join(", ")}` : acceptArr[0]}`
	};
};
function isDataTransferItemWithEmptyType(file) {
	return file.type === "" && typeof file.getAsFile === "function";
}
function srcFileAccepted(file, accept) {
	if (file.type === "application/x-moz-file" || accepts(file, accept) || isDataTransferItemWithEmptyType(file)) return [true, null];
	return [false, getInvalidTypeRejectionErr(accept)];
}
function isDefined(value) {
	return value !== void 0 && value !== null;
}
function isFileError(value) {
	return isDefined(value);
}
var getTooLargeRejectionErr = (maxSize) => {
	return {
		code: FILE_TOO_LARGE,
		message: `File is larger than ${maxSize} ${maxSize === 1 ? "byte" : "bytes"}`
	};
};
var getTooSmallRejectionErr = (minSize) => {
	return {
		code: FILE_TOO_SMALL,
		message: `File is smaller than ${minSize} ${minSize === 1 ? "byte" : "bytes"}`
	};
};
function srcFileMatchSize(file, minSize, maxSize) {
	if (isDefined(file.size)) {
		if (isDefined(minSize) && isDefined(maxSize)) {
			if (file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
			if (file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];
		} else if (isDefined(minSize) && file.size < minSize) return [false, getTooSmallRejectionErr(minSize)];
		else if (isDefined(maxSize) && file.size > maxSize) return [false, getTooLargeRejectionErr(maxSize)];
	}
	return [true, null];
}
function fileCountInRange(files, maxFiles) {
	if (files.length <= maxFiles) return [true, null];
	return [false, TOO_MANY_FILES_REJECTION];
}
function fileTypeAccepted(file, accept) {
	return srcFileAccepted(file, acceptPropAsAcceptAttr(accept));
}
function fileMatchSize(file, minSize, maxSize) {
	return srcFileMatchSize(file, minSize, maxSize);
}
function acceptRejectFiles(files, accept, maxSize, maxFiles = Infinity) {
	const [fileCountAccepted, fileCountError] = fileCountInRange(files, maxFiles);
	return files.reduce((result, file) => {
		const [typeAccepted, fileTypeError] = fileTypeAccepted(file, accept);
		const [sizeMatch, sizeError] = fileMatchSize(file, 0, maxSize);
		if (fileCountAccepted && typeAccepted && sizeMatch) result.acceptedFiles.push(file);
		else {
			const errors = [
				fileTypeError,
				sizeError,
				fileCountError
			].filter(isFileError);
			result.fileRejections.push({
				file,
				errors
			});
		}
		return result;
	}, {
		acceptedFiles: [],
		fileRejections: []
	});
}
//#endregion
//#region src/uploader/lib/error.ts
var RejectionErrorCode = /* @__PURE__ */ function(RejectionErrorCode) {
	RejectionErrorCode["InvalidType"] = "INVALID_TYPE";
	RejectionErrorCode["ExceededMaxSize"] = "EXCEEDED_MAX_SIZE";
	RejectionErrorCode["ExceededMaxFiles"] = "EXCEEDED_MAX_FILES";
	RejectionErrorCode["Unknown"] = "UNKNOWN";
	return RejectionErrorCode;
}({});
function errorMessageFromCode(errorCode) {
	switch (errorCode) {
		case "INVALID_TYPE": return "One or more files are not the correct type.";
		case "EXCEEDED_MAX_SIZE": return "One or more files are too large.";
		case "EXCEEDED_MAX_FILES": return "Maximum number of files has been exceeded.";
		case "UNKNOWN": return "Unknown Error";
	}
}
//#endregion
//#region src/uploader/components/DropZone.tsx
function noop() {}
var DropArea = React.forwardRef((props, ref) => /* @__PURE__ */ React.createElement(Box, {
	ref,
	...props,
	"aria-label": "File upload dropzone",
	css: {
		position: "relative",
		outline: "none"
	}
}));
DropArea.displayName = "ZDocsUploaderDropArea";
var DropZoneOverlay = (props) => /* @__PURE__ */ React.createElement(Box, {
	...props,
	css: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background: "{colors.bg.action.hero.hover}",
		opacity: "25%"
	}
});
function toErrorCode(str) {
	switch (str) {
		case FILE_INVALID_TYPE: return RejectionErrorCode.InvalidType;
		case FILE_TOO_LARGE: return RejectionErrorCode.ExceededMaxSize;
		case TOO_MANY_FILES: return RejectionErrorCode.ExceededMaxFiles;
		default: return RejectionErrorCode.Unknown;
	}
}
function toFileRejections(rejections, hasAccepts) {
	return {
		partial: hasAccepts,
		rejections: rejections.map((r) => {
			return {
				file: r.file,
				errorCodes: r.errors.map((e) => toErrorCode(e.code))
			};
		})
	};
}
function DropZone({ onFileAccepted, onFileRejected, renderer, accept, maxSize, maxFiles = Infinity, className }) {
	const onPaste = useCallback((files) => {
		const { acceptedFiles, fileRejections } = acceptRejectFiles(files, accept, maxSize, maxFiles);
		if (!!onFileAccepted && acceptedFiles.length > 0) onFileAccepted(acceptedFiles);
		if (!!onFileRejected && fileRejections.length > 0) onFileRejected(toFileRejections(fileRejections, acceptedFiles.length > 0));
	}, [
		onFileAccepted,
		onFileRejected,
		accept,
		maxSize,
		maxFiles
	]);
	useEffect(() => {
		function onFilePaste(evt) {
			if (evt instanceof ClipboardEvent) {
				const evtFiles = evt?.clipboardData?.files || [];
				const files = evtFiles instanceof FileList ? Array.from(evtFiles) : evtFiles;
				onPaste(files);
			}
		}
		window.addEventListener("paste", onFilePaste);
		return () => {
			window.removeEventListener("paste", onFilePaste);
		};
	}, [onPaste]);
	const [isWindowDrag, setWindowDrag] = useState(false);
	useEffect(() => {
		function onDocumentDragOver() {
			setWindowDrag(true);
		}
		function onDocumentDragLeave() {
			setWindowDrag(false);
		}
		window.addEventListener("dragover", onDocumentDragOver, false);
		window.addEventListener("dragleave", onDocumentDragLeave, false);
		window.addEventListener("drop", onDocumentDragLeave, false);
		return () => {
			window.removeEventListener("dragover", onDocumentDragOver);
			window.removeEventListener("dragleave", onDocumentDragLeave);
			window.removeEventListener("drop", onDocumentDragLeave);
		};
	}, []);
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: useCallback((acceptedFiles, fileRejections) => {
			const [fileCountAccepted, fileCountError] = fileCountInRange(acceptedFiles, maxFiles);
			if (!fileCountAccepted) {
				const fileCountRejections = acceptedFiles.map((file) => {
					return {
						file,
						errors: [fileCountError]
					};
				});
				acceptedFiles = [];
				fileRejections = fileRejections.concat(fileCountRejections);
			}
			if (!!onFileAccepted && acceptedFiles.length > 0) onFileAccepted(acceptedFiles);
			if (!!onFileRejected && fileRejections.length > 0) onFileRejected(toFileRejections(fileRejections, acceptedFiles.length > 0));
		}, [
			onFileAccepted,
			onFileRejected,
			maxFiles
		]),
		multiple: maxFiles > 1,
		accept,
		maxSize,
		maxFiles
	});
	const isDragOutside = isWindowDrag && !isDragActive;
	const isDragInside = isDragActive;
	const rootProps = getRootProps();
	const inputProps = { ...getInputProps() };
	return /* @__PURE__ */ React.createElement(DropArea, {
		...rootProps,
		onClick: noop,
		tabIndex: void 0,
		className
	}, /* @__PURE__ */ React.createElement("input", inputProps), renderer({
		isDragOutside,
		isDragInside,
		onAddFiles: rootProps?.onClick ?? noop
	}));
}
//#endregion
//#region src/uploader/lib/constants.ts
var DEFAULT_PLACEHOLDER_TEXT = "Upload images, PDFs or Word documents. Each file must be smaller than 100 MB.";
//#endregion
//#region src/uploader/components/DropZonePlaceholder.tsx
var Container$2 = ({ isDragOutside, isDragInside, children, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		position: "relative",
		outline: "none",
		height: "15rem",
		display: "flex",
		justifyContent: "center",
		border: isDragInside ? "2px dashed {colors.border.action.hero.hover}" : isDragOutside ? "1px solid {colors.border.action.hero.hover}" : "2px dashed {colors.border.muted}",
		borderRadius: "{radii.obj.default}"
	},
	...props
}, children);
var Placeholder = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		maxWidth: "32rem",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		textAlign: "center"
	},
	...props
});
var AddFilesButton = (props) => /* @__PURE__ */ React.createElement(Button, {
	css: {
		width: "10rem",
		margin: "1.75rem auto 1rem auto",
		whiteSpace: "nowrap"
	},
	...props
});
function DropZonePlaceholder({ isDragOutside, isDragInside, canAddFiles, onAddFiles, addFilesDescription, placeholderText = DEFAULT_PLACEHOLDER_TEXT, className }) {
	return /* @__PURE__ */ React.createElement(Container$2, {
		isDragOutside,
		isDragInside,
		className
	}, isDragInside && /* @__PURE__ */ React.createElement(DropZoneOverlay, null), /* @__PURE__ */ React.createElement(Placeholder, null, /* @__PURE__ */ React.createElement(Text, null, placeholderText), /* @__PURE__ */ React.createElement(AddFilesButton, {
		emphasis: "filled",
		type: "button",
		disabled: !canAddFiles,
		onClick: onAddFiles,
		icon: /* @__PURE__ */ React.createElement(IconPlusOutline, null)
	}, addFilesDescription), /* @__PURE__ */ React.createElement(Text, { textStyle: "body-sm" }, "You may also drop files here or paste.")));
}
//#endregion
//#region src/uploader/components/FilePreview.tsx
var FileItemPreviewImg = (props) => /* @__PURE__ */ React.createElement(Box, {
	asChild: true,
	css: {
		display: "block",
		maxWidth: "100%",
		maxHeight: "100%",
		width: "auto",
		height: "auto",
		margin: "auto"
	}
}, /* @__PURE__ */ React.createElement("img", props));
var FileItemPreviewIcon = ({ hasError, ...props }) => /* @__PURE__ */ React.createElement(Icon, {
	css: {
		display: "block",
		margin: "auto",
		color: hasError ? "{colors.icon.feedback.critical.strong}" : "{colors.icon.accent.brand.hero}"
	},
	...props
});
function FilePreview({ file, hasError }) {
	const fileType = file.type ?? "";
	const isImage = fileType.startsWith("image/");
	const FileIconComponent = fileType === "application/pdf" ? IconFilePDFOutline : IconFileOutline;
	/**
	* If we fail to load the image, flip back to the default file icon.
	*/
	const [hasImageLoadError, setHasImageLoadError] = useState(false);
	function handleOnError() {
		setHasImageLoadError(true);
	}
	/**
	* Load the preview URL and clean it up carefully to avoid memory leaks.
	*/
	const [previewUrl, setPreviewUrl] = useState("");
	useEffect(() => {
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		setHasImageLoadError(false);
		return () => {
			URL.revokeObjectURL(url);
			setPreviewUrl("");
		};
	}, [file]);
	return isImage && !hasImageLoadError ? /* @__PURE__ */ React.createElement(FileItemPreviewImg, {
		src: previewUrl,
		alt: file.name ?? "",
		onError: handleOnError
	}) : /* @__PURE__ */ React.createElement(FileItemPreviewIcon, {
		size: "xl",
		hasError
	}, /* @__PURE__ */ React.createElement(FileIconComponent, null));
}
//#endregion
//#region src/uploader/lib/upload-manager.ts
var UPLOAD_URL = "/api/v1/documents/actions/upload";
var DEFAULT_BASE_URL = "https://www.zillowdocs.com";
var FileUploadStatus = /* @__PURE__ */ function(FileUploadStatus) {
	FileUploadStatus["Rejected"] = "Rejected";
	FileUploadStatus["Pending"] = "Pending";
	FileUploadStatus["Uploading"] = "Uploading";
	FileUploadStatus["Completed"] = "Completed";
	FileUploadStatus["Failed"] = "Failed";
	return FileUploadStatus;
}({});
var ErrorCode = /* @__PURE__ */ function(ErrorCode) {
	ErrorCode["Client"] = "exception.client";
	ErrorCode["Server"] = "exception.server";
	ErrorCode["Upload"] = "exception.upload";
	ErrorCode["EncryptedPdf"] = "exception.pdf.encrypted";
	return ErrorCode;
}({});
var ZDocsUploadManager = class {
	generateUploadToken;
	onChange;
	onProgress;
	onUploadActive;
	onUploadComplete;
	onUploadFailed;
	queueSize;
	maxRetries;
	baseUrl;
	fileIds;
	uploads;
	uploadToken = null;
	uploadTokenPromise;
	uploadTokenPromisePending = false;
	pendingPromiseStates;
	isUploadActive;
	notifiedComplete;
	notifiedFailed;
	isProcessingState;
	retryTimeoutId;
	processingGroupId = -1;
	constructor({ generateUploadToken, onChange, onProgress, onUploadActive, onUploadComplete, onUploadFailed, queueSize = 1, maxRetries = 0, baseUrl = DEFAULT_BASE_URL }) {
		this.generateUploadToken = generateUploadToken;
		this.onChange = onChange;
		this.onProgress = onProgress;
		this.onUploadActive = onUploadActive;
		this.onUploadComplete = onUploadComplete;
		this.onUploadFailed = onUploadFailed;
		this.queueSize = queueSize;
		this.maxRetries = maxRetries;
		this.baseUrl = baseUrl;
		this.fileIds = /* @__PURE__ */ new WeakMap();
		this.uploads = [];
		this.pendingPromiseStates = [];
		this.isUploadActive = false;
		this.notifiedComplete = [];
		this.notifiedFailed = [];
		this.isProcessingState = false;
	}
	getFileId(file) {
		const { fileIds } = this;
		const fileId = fileIds.get(file);
		if (fileId) return fileId;
		const uniqueId = nanoid();
		fileIds.set(file, uniqueId);
		return uniqueId;
	}
	async uploadFiles(files) {
		const groupId = ++this.processingGroupId;
		const uploadingGroup = [];
		const retryGroup = [];
		const newFiles = files instanceof FileList ? Array.from(files) : Array.isArray(files) ? files : [files];
		this.uploads.forEach((upload) => {
			const fileIndex = newFiles.indexOf(upload.file);
			const isExistingFile = fileIndex >= 0;
			const isRetry = isExistingFile && upload.status === "Failed";
			const isUploading = isRetry || upload.status === "Pending" || upload.status === "Uploading";
			if (isExistingFile) newFiles.splice(fileIndex, 1);
			if (isUploading) {
				upload.processingGroupId = groupId;
				uploadingGroup.push(upload);
			}
			if (isRetry) {
				upload.status = "Pending";
				upload.progress = 0;
				upload.errorMessage = null;
				upload.errorCode = null;
				upload.retryCount = 0;
				retryGroup.push(upload);
				const index = this.notifiedFailed.indexOf(upload);
				if (index >= 0) this.notifiedFailed.splice(index, 1);
			}
		});
		const newUploads = newFiles.map((file) => {
			return {
				file,
				fileId: this.getFileId(file),
				processingGroupId: groupId,
				progress: 0,
				status: "Pending",
				retryCount: 0
			};
		});
		this.uploads = [...this.uploads, ...newUploads];
		this._onChange([...uploadingGroup, ...newUploads]);
		const promise = new Promise((resolve) => {
			this.pendingPromiseStates.push({
				resolve,
				uploads: [...retryGroup, ...newUploads]
			});
		});
		this._processUploadState();
		return promise;
	}
	deleteFile(file) {
		const index = this.uploads.findIndex((upload) => upload.file === file);
		if (index === -1) return;
		const { status } = this.uploads[index];
		if (status !== "Failed" && status !== "Rejected") return;
		const deletedFiles = this.uploads.splice(index, 1);
		this._onChange(deletedFiles);
	}
	disconnect() {
		this._cancelRetry();
		this.onChange = void 0;
		this.onProgress = void 0;
		this.onUploadActive = void 0;
		this.onUploadComplete = void 0;
		this.onUploadFailed = void 0;
		this.generateUploadToken = () => Promise.resolve("");
	}
	_getUploadToken() {
		if (this.uploadToken) {
			this.uploadTokenPromise = Promise.resolve(this.uploadToken);
			this.uploadTokenPromisePending = false;
		} else if (!this.uploadTokenPromise || !this.uploadTokenPromisePending) {
			this.uploadTokenPromisePending = true;
			this.uploadTokenPromise = this.generateUploadToken().then((uploadToken) => {
				this.uploadToken = uploadToken;
				return uploadToken;
			}).finally(() => {
				this.uploadTokenPromisePending = false;
			});
		}
		return this.uploadTokenPromise;
	}
	async _startUpload(pendingUpload) {
		const file = pendingUpload.file;
		this._updateFileData(file, {
			progress: 0,
			status: "Uploading"
		});
		let uploadToken;
		try {
			uploadToken = await this._getUploadToken();
			if (!uploadToken) throw Error("No Upload Token");
			const formData = new FormData();
			formData.set("file", file);
			const fileRequest = new XMLHttpRequest();
			fileRequest.open("POST", `${this.baseUrl}${UPLOAD_URL}`);
			fileRequest.setRequestHeader("Accept", "application/json");
			fileRequest.setRequestHeader("Authorization", `Bearer ${uploadToken}`);
			fileRequest.setRequestHeader("x-zdocs-sdk-web", `469bef48beb344ce31fcd0c8b627ad7927db444b`);
			fileRequest.onload = async () => {
				this._handleUploadResult(file, fileRequest);
			};
			fileRequest.onerror = (_e) => {
				const clientError = this._getClientError(fileRequest.responseText);
				this._handleUploadError(file, clientError);
			};
			fileRequest.upload.onprogress = (e) => {
				if (e.lengthComputable) {
					const upload = this._getUpload(file);
					if (upload) {
						const progress = Math.ceil(e.loaded / e.total * 100);
						this._updateFileData(file, { progress });
						this._onProgress(upload);
					}
				}
			};
			fileRequest.send(formData);
		} catch (e) {
			this.uploadToken = null;
			const initializationError = this._getInitializationError(e);
			this._handleUploadError(file, initializationError);
		}
	}
	_cancelRetry() {
		if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
		this.retryTimeoutId = void 0;
	}
	_handleUploadResult(file, fileRequest) {
		const { responseText, status } = fileRequest;
		const { success, errors, failed } = this._getUploadResponseJson(responseText) ?? {};
		if (status !== 200 || success?.length !== 1) {
			const uploadError = this._getUploadError(status, errors, failed);
			if (status === 401 || status === 403) this.uploadToken = null;
			this._handleUploadError(file, uploadError, fileRequest.status);
			return;
		}
		const { messageToken } = success[0];
		this._updateFileData(file, {
			status: "Completed",
			progress: 100,
			messageToken: messageToken || null
		});
	}
	_getUploadResponseJson(responseText) {
		try {
			return JSON.parse(responseText);
		} catch (e) {}
	}
	_getClientError(responseText) {
		return {
			errorType: "Client Error",
			errorCode: "exception.client",
			errorMessage: responseText || "Unknown Client Error"
		};
	}
	_getInitializationError(error) {
		return {
			errorType: "Server Error",
			errorCode: "exception.server",
			errorMessage: error instanceof Error && error.message ? error.message : "Unknown Initialization Error"
		};
	}
	_getUploadFileError(fileErrors) {
		const { message, code } = fileErrors?.[0] ?? {};
		return {
			errorType: "Server Error",
			errorCode: code || "exception.upload",
			errorMessage: message || "Unsuccessful Upload"
		};
	}
	_getDefaultServerErrorMessage(statusCode) {
		switch (statusCode) {
			case 0: return "No Response";
			case 400: return "Bad Request";
			case 401: return "Unauthorized";
			case 403: return "Forbidden";
			case 408: return "Request Timeout";
			case 500: return "Server Error";
		}
		return "Unhandled Response";
	}
	_getUploadServerError(statusCode, serverErrors) {
		const { errorMessage } = serverErrors?.[0] ?? {};
		return {
			errorType: "Server Error",
			errorCode: "exception.upload",
			errorMessage: errorMessage || this._getDefaultServerErrorMessage(statusCode)
		};
	}
	_getUploadError(statusCode, serverErrors, fileErrors) {
		if (statusCode === 200) return this._getUploadFileError(fileErrors);
		return this._getUploadServerError(statusCode, serverErrors);
	}
	_handleUploadError(file, uploadError, requestStatus) {
		const upload = this._getUpload(file);
		if (!upload) return;
		const fileData = this._getUploadFailureFileData(upload, uploadError, requestStatus);
		if (fileData.status === "Pending") {
			this._cancelRetry();
			this.retryTimeoutId = setTimeout(() => {
				this.retryTimeoutId = void 0;
				this._updateFileData(file, fileData);
			}, 1e3);
			return;
		}
		this._updateFileData(file, fileData);
	}
	_getUploadFailureStatus(upload, errorCode) {
		if (errorCode === "exception.pdf.encrypted") return "Rejected";
		if (upload.retryCount < this.maxRetries) return "Pending";
		return "Failed";
	}
	_getUploadFailureFileData(upload, uploadError, requestStatus) {
		const { retryCount } = upload;
		const { errorCode, errorMessage } = uploadError;
		const status = this._getUploadFailureStatus(upload, errorCode);
		if (status === "Pending") return {
			status,
			progress: 0,
			messageToken: null,
			errorMessage,
			retryCount: retryCount + 1
		};
		return {
			...uploadError,
			status,
			progress: 100,
			statusCode: requestStatus,
			messageToken: null
		};
	}
	_getUpload(file) {
		return this.uploads.find((upload) => upload.file === file);
	}
	_onProgress(upload) {
		if (this.onProgress) this.onProgress(upload);
	}
	_updateFileData(file, { progress, status, messageToken, errorType, errorMessage, statusCode, retryCount, errorCode }) {
		const upload = this._getUpload(file);
		if (!upload) return;
		if (progress !== void 0) upload.progress = progress;
		if (status !== void 0) upload.status = status;
		if (messageToken !== void 0) upload.messageToken = messageToken;
		if (errorType !== void 0) upload.errorType = errorType;
		if (errorMessage !== void 0) upload.errorMessage = errorMessage;
		if (errorCode !== void 0) upload.errorCode = errorCode;
		if (statusCode !== void 0) upload.statusCode = statusCode;
		if (retryCount !== void 0) upload.retryCount = retryCount;
		this._onChange([upload]);
		this._processUploadState();
	}
	_onChange(changedUploads) {
		if (this.onChange) this.onChange(changedUploads, [...this.uploads], this.processingGroupId);
	}
	_processUploadState() {
		if (!this.isProcessingState) {
			this.isProcessingState = true;
			this._doProcessUploadState();
			this.isProcessingState = false;
		}
	}
	_doProcessUploadState() {
		const { pending, uploading, completed, failed } = this._uploadsByStatus();
		const stateIsUploading = pending.length > 0 || uploading.length > 0;
		if (stateIsUploading !== this.isUploadActive) {
			this.isUploadActive = stateIsUploading;
			if (this.onUploadActive) this.onUploadActive(this.isUploadActive);
		}
		completed.forEach((upload) => {
			if (!this.notifiedComplete.includes(upload)) {
				this.notifiedComplete.push(upload);
				if (this.onUploadComplete) this.onUploadComplete(upload);
			}
		});
		failed.forEach((upload) => {
			if (!this.notifiedFailed.includes(upload)) {
				this.notifiedFailed.push(upload);
				if (this.onUploadFailed) this.onUploadFailed(upload);
			}
		});
		for (let i = 0; i < this.pendingPromiseStates.length; i++) {
			const pendingPromiseState = this.pendingPromiseStates[i];
			if (pendingPromiseState.uploads.every((upload) => upload.status === "Completed" || upload.status === "Failed" || upload.status === "Rejected")) {
				pendingPromiseState.resolve(pendingPromiseState.uploads);
				this.pendingPromiseStates.splice(i, 1);
				i--;
			}
		}
		if (uploading.length < this.queueSize) {
			const queueCapacity = this.queueSize - uploading.length;
			for (let i = 0; i < pending.length && i < queueCapacity; i++) this._startUpload(pending[i]);
		}
	}
	_uploadsByStatus() {
		const uploadsByStatus = this.uploads.reduce((result, upload) => {
			if (upload.status === "Pending") result.pending.push(upload);
			else if (upload.status === "Uploading") result.uploading.push(upload);
			else if (upload.status === "Completed") result.completed.push(upload);
			else if (upload.status === "Failed" || upload.status === "Rejected") result.failed.push(upload);
			return result;
		}, {
			pending: [],
			uploading: [],
			completed: [],
			failed: []
		});
		uploadsByStatus.pending.sort((a, b) => a.retryCount - b.retryCount);
		return uploadsByStatus;
	}
};
//#endregion
//#region ../core/lib/filesize.ts
var UNITS = {
	diskSpace: [
		"B",
		"KB",
		"MB",
		"GB",
		"TB",
		"PB",
		"EB"
	],
	integer: [
		"",
		"k",
		"M",
		"B",
		"T"
	]
};
function filesize_default(size, fixed = 1, useSi = false, integer = false) {
	const radix = useSi || integer ? 1e3 : 1024;
	const units = integer ? UNITS.integer : UNITS.diskSpace;
	const spacer = integer ? "" : " ";
	let bytes = Math.abs(size);
	let loop = 0;
	while (bytes >= radix) {
		bytes /= radix;
		++loop;
	}
	return `${!loop && integer ? bytes : bytes.toFixed(fixed)}${spacer}${units[loop]}`;
}
//#endregion
//#region src/uploader/lib/file-upload.ts
var FileUploadGroupType = /* @__PURE__ */ function(FileUploadGroupType) {
	FileUploadGroupType[FileUploadGroupType["Rejected"] = 0] = "Rejected";
	FileUploadGroupType[FileUploadGroupType["Pending"] = 1] = "Pending";
	FileUploadGroupType[FileUploadGroupType["Processing"] = 2] = "Processing";
	FileUploadGroupType[FileUploadGroupType["Failed"] = 3] = "Failed";
	FileUploadGroupType[FileUploadGroupType["Completed"] = 4] = "Completed";
	return FileUploadGroupType;
}({});
function getClientErrorDescription(errorCode, maxFileSize) {
	switch (errorCode) {
		case RejectionErrorCode.InvalidType: return "Unsupported file type";
		case RejectionErrorCode.ExceededMaxSize: return `File exceeds ${filesize_default(maxFileSize)} size limit`;
		case RejectionErrorCode.ExceededMaxFiles: return "Maximum number of files has been exceeded";
		default: return "File rejected";
	}
}
function getServerErrorDescription(errorCode) {
	if (errorCode === ErrorCode.EncryptedPdf) return "Encrypted or corrupted PDF";
	return "File rejected";
}
function getProcessingStatusDescription(upload) {
	const { status, progress, errorCode, file: { size: fileSize } } = upload;
	switch (status) {
		case FileUploadStatus.Pending: return "Waiting...";
		case FileUploadStatus.Uploading: return `${progress}% Complete`;
		case FileUploadStatus.Completed: return filesize_default(fileSize);
		case FileUploadStatus.Rejected: return getServerErrorDescription(errorCode);
		case FileUploadStatus.Failed: return "Retry upload or delete";
	}
}
function createClientRejectedFileUpload(fileId, fileRejection, maxFileSize) {
	const { file, errorCodes: [errorCode] } = fileRejection;
	const statusDescription = getClientErrorDescription(errorCode, maxFileSize);
	return {
		fileId,
		file,
		groupType: 0,
		status: FileUploadStatus.Rejected,
		statusDescription,
		errorCode,
		canDelete: true
	};
}
function createServerRejectedFileUpload(fileId, upload) {
	const { file, errorCode } = upload;
	const statusDescription = getServerErrorDescription(errorCode);
	return {
		fileId,
		file,
		groupType: 0,
		status: FileUploadStatus.Rejected,
		statusDescription,
		errorCode: errorCode || ErrorCode.Upload,
		canDelete: true
	};
}
function createPendingFileUpload(fileId, file) {
	return {
		fileId,
		file,
		groupType: 1,
		status: FileUploadStatus.Pending,
		statusDescription: filesize_default(file.size),
		progress: 0,
		canDelete: true
	};
}
function createProcessingFileUpload(fileId, upload) {
	const { status, progress, file, errorCode } = upload;
	return {
		fileId,
		file,
		groupType: 2,
		status,
		statusDescription: getProcessingStatusDescription(upload),
		progress,
		errorCode,
		canDelete: false
	};
}
function createFailedFileUpload(fileId, upload) {
	const status = FileUploadStatus.Failed;
	const progress = 100;
	const { file } = upload;
	return {
		fileId,
		file,
		groupType: 3,
		status,
		statusDescription: getProcessingStatusDescription(upload),
		progress,
		canDelete: true
	};
}
function createCompletedFileUpload(fileId, upload) {
	const status = FileUploadStatus.Completed;
	const progress = 100;
	const { file } = upload;
	return {
		fileId,
		file,
		groupType: 4,
		status,
		statusDescription: getProcessingStatusDescription(upload),
		progress,
		canDelete: false
	};
}
function toPendingFileUpload(fileUpload) {
	return {
		...fileUpload,
		groupType: 1,
		status: FileUploadStatus.Pending,
		statusDescription: filesize_default(fileUpload.file.size),
		progress: 0,
		canDelete: true
	};
}
function getFilesInflection(fileCount) {
	return fileCount === 1 ? "file" : "files";
}
function getUploadsInflection(fileCount) {
	return fileCount === 1 ? "upload" : "uploads";
}
function getFileUploadGroupDescription(groupType, uploadCount) {
	const fileText = getFilesInflection(uploadCount);
	const uploadText = getUploadsInflection(uploadCount);
	switch (groupType) {
		case 0: return `Cannot upload ${uploadCount} ${fileText}`;
		case 1: return `${uploadCount} ${fileText} pending`;
		case 2: return `Uploading ${uploadCount} ${fileText}`;
		case 3: return `${uploadCount} ${uploadText} failed`;
		case 4: return `${uploadCount} ${uploadText} complete`;
	}
}
function createRejectedFileUploadGroup(groupFiles) {
	const groupType = 0;
	return {
		groupType,
		groupFiles,
		description: getFileUploadGroupDescription(groupType, groupFiles.length)
	};
}
function createPendingFileUploadGroup(groupFiles) {
	const groupType = 1;
	return {
		groupType,
		groupFiles,
		description: getFileUploadGroupDescription(groupType, groupFiles.length)
	};
}
function createProcessingFileUploadGroup(groupFiles) {
	const groupType = 2;
	return {
		groupType,
		groupFiles,
		description: getFileUploadGroupDescription(groupType, groupFiles.length)
	};
}
function createFailedFileUploadGroup(groupFiles) {
	const groupType = 3;
	return {
		groupType,
		groupFiles,
		description: getFileUploadGroupDescription(groupType, groupFiles.length)
	};
}
function createCompletedFileUploadGroup(groupFiles) {
	const groupType = 4;
	return {
		groupType,
		groupFiles,
		description: getFileUploadGroupDescription(groupType, groupFiles.length)
	};
}
//#endregion
//#region src/uploader/components/FileItem.tsx
var iconMixin = { "@media (min-width: 501px)": { "&&& svg": {
	height: "1rem",
	width: "1rem"
} } };
var Container$1 = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		"--_preview-max-height": "8.75rem",
		width: "100%",
		display: "grid",
		gridTemplateAreas: `
        'preview preview'
        'name indicator'
        'description indicator'
      `,
		gridTemplateColumns: "1fr auto",
		gridTemplateRows: "var(--_preview-max-height) auto auto",
		alignItems: "center",
		columnGap: "0.5rem",
		"@media (max-width: 500px)": {
			"--_preview-max-height": "5rem",
			gridTemplateAreas: `
          'preview name indicator'
          'preview description indicator'
        `,
			gridTemplateColumns: "var(--_preview-max-height) 1fr auto",
			gridTemplateRows: "repeat(2, minmax(2.5rem, 1fr))"
		}
	},
	...props
});
var Preview = ({ hasError, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		gridArea: "preview",
		width: "100%",
		height: "100%",
		maxHeight: "var(--_preview-max-height)",
		position: "relative",
		borderRadius: "{radii.obj.default}",
		display: "flex",
		background: "{colors.bg.softest}",
		border: hasError ? "solid 2px {colors.border.feedback.critical.strong}" : "solid 1px {colors.border.muted}",
		boxShadow: "{shadows.sm}"
	},
	...props
});
var Name = (props) => /* @__PURE__ */ React.createElement(Text, {
	css: {
		gridArea: "name",
		alignSelf: "end",
		"@media (min-width: 501px)": { paddingTop: "0.5rem" }
	},
	...props
});
var Description$2 = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		gridArea: "description",
		alignSelf: "start",
		display: "flex",
		alignItems: "center",
		paddingTop: "0.25rem"
	},
	...props
});
var Indicator$1 = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		gridArea: "indicator",
		...iconMixin
	},
	...props
});
var UploadProcessing = () => /* @__PURE__ */ React.createElement(Box, { css: {
	position: "absolute",
	top: 0,
	bottom: 0,
	left: 0,
	right: 0,
	display: "flex",
	background: "{colors.bg.softer}",
	borderRadius: "{radii.obj.default}",
	opacity: .9
} }, /* @__PURE__ */ React.createElement(Spinner, { css: {
	margin: "auto",
	width: "40px",
	height: "40px"
} }));
function FileItem({ fileUpload, onDelete, acceptDescription, className }) {
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);
	const isProcessing = fileUpload.groupType === FileUploadGroupType.Processing && (fileUpload.status === FileUploadStatus.Pending || fileUpload.status === FileUploadStatus.Uploading);
	const isCompleted = fileUpload.status === FileUploadStatus.Completed;
	const isRejected = fileUpload.status === FileUploadStatus.Rejected;
	const isInvalidType = isRejected && fileUpload.errorCode === RejectionErrorCode.InvalidType;
	const hasError = isRejected || fileUpload.status === FileUploadStatus.Failed;
	return /* @__PURE__ */ React.createElement(Container$1, { className }, /* @__PURE__ */ React.createElement(Preview, { hasError }, fileUpload.file && /* @__PURE__ */ React.createElement(FilePreview, {
		file: fileUpload.file,
		hasError
	}), isProcessing && /* @__PURE__ */ React.createElement(UploadProcessing, null)), /* @__PURE__ */ React.createElement(Name, {
		asChild: true,
		textStyle: "body-xs-bold",
		title: fileUpload.file.name
	}, /* @__PURE__ */ React.createElement("strong", null, fileUpload.file.name)), /* @__PURE__ */ React.createElement(Description$2, null, /* @__PURE__ */ React.createElement(Text, {
		textStyle: "body-xs",
		css: {
			color: hasError ? "{colors.text.feedback.critical.strong}" : "",
			paddingRight: "0.5rem"
		}
	}, fileUpload.statusDescription), isInvalidType && acceptDescription && /* @__PURE__ */ React.createElement(Popover, {
		open: isTooltipOpen,
		onOpenChange: setIsTooltipOpen,
		header: /* @__PURE__ */ React.createElement(Heading, { level: 4 }, "Supported Types"),
		body: /* @__PURE__ */ React.createElement(Paragraph, null, acceptDescription),
		trigger: /* @__PURE__ */ React.createElement(TextButton, {
			tone: "brand",
			type: "button",
			title: "Supported Types",
			css: iconMixin,
			icon: /* @__PURE__ */ React.createElement(IconQuestionMarkCircleOutline, null)
		}, /* @__PURE__ */ React.createElement(React.Fragment, null))
	})), /* @__PURE__ */ React.createElement(Indicator$1, null, fileUpload.canDelete ? /* @__PURE__ */ React.createElement(TextButton, {
		tone: "brand",
		type: "button",
		title: "Delete",
		icon: /* @__PURE__ */ React.createElement(IconDeleteOutline, null),
		onClick: () => onDelete(fileUpload)
	}, /* @__PURE__ */ React.createElement(React.Fragment, null)) : isCompleted ? /* @__PURE__ */ React.createElement(Icon, {
		size: "md",
		css: { color: "{colors.icon.feedback.success.hero}" }
	}, /* @__PURE__ */ React.createElement(IconCheckmarkCircleFilled, null)) : null));
}
//#endregion
//#region src/uploader/components/FileItemGroups.tsx
var Header = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "grid",
		gridTemplateColumns: "5fr minmax(auto, 1fr)",
		paddingBottom: "0.75rem",
		minHeight: "2.5rem"
	},
	...props
});
var Description$1 = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "flex",
		whiteSpace: "nowrap"
	},
	...props
});
var StyledIconWarning = (props) => /* @__PURE__ */ React.createElement(Icon, {
	css: {
		marginRight: "0.25rem",
		color: "{colors.icon.feedback.critical.strong}"
	},
	...props
});
var Action = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		textAlign: "right",
		whiteSpace: "nowrap"
	},
	...props
});
var Files = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(210.5px, 1fr))",
		justifyItems: "center",
		alignItems: "start",
		gridGap: "1rem",
		"@media (max-width: 500px)": { rowGap: "0.5rem" }
	},
	...props
});
var RenderFileContainer = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: { width: "100%" },
	...props
});
function FileItemGroup({ fileGroup, renderFile, acceptDescription, canAddFiles, onAddFiles, addFilesDescription, onDelete, className }) {
	return /* @__PURE__ */ React.createElement(Box, { className }, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement(Description$1, null, (fileGroup.groupType === FileUploadGroupType.Rejected || fileGroup.groupType === FileUploadGroupType.Failed) && /* @__PURE__ */ React.createElement(StyledIconWarning, { size: "md" }, /* @__PURE__ */ React.createElement(IconWarningFilled, null)), /* @__PURE__ */ React.createElement(Text, { textStyle: "body-bold" }, fileGroup.description)), /* @__PURE__ */ React.createElement(Action, null, canAddFiles && /* @__PURE__ */ React.createElement(TextButton, {
		type: "button",
		textStyle: "body-bold",
		onClick: onAddFiles,
		icon: /* @__PURE__ */ React.createElement(IconPlusFilled, null)
	}, addFilesDescription))), /* @__PURE__ */ React.createElement(Files, null, fileGroup.groupFiles.map((fileUpload) => /* @__PURE__ */ React.createElement(Fragment, { key: fileUpload.fileId }, renderFile ? /* @__PURE__ */ React.createElement(RenderFileContainer, null, renderFile({
		fileUpload,
		acceptDescription,
		onDelete
	})) : /* @__PURE__ */ React.createElement(FileItem, {
		fileUpload,
		acceptDescription,
		onDelete
	})))));
}
var Container = ({ isDragging, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		outline: "1px solid",
		outlineColor: isDragging ? "{colors.border.action.hero.hover}" : "transparent",
		display: "flex",
		flexDirection: "column",
		rowGap: "1rem"
	},
	...props
});
function FileItemGroups({ isDragOutside, isDragInside, fileGroups, acceptDescription, canAddFiles, onAddFiles, addFilesDescription, renderFile, onDelete, className }) {
	return /* @__PURE__ */ React.createElement(Container, {
		className,
		isDragging: isDragOutside || isDragInside
	}, isDragInside && /* @__PURE__ */ React.createElement(DropZoneOverlay, null), fileGroups.map((fileGroup, groupIndex) => /* @__PURE__ */ React.createElement(FileItemGroup, {
		key: fileGroup.groupType,
		fileGroup,
		acceptDescription,
		canAddFiles: canAddFiles && groupIndex === 0,
		onAddFiles,
		addFilesDescription,
		renderFile,
		onDelete
	})));
}
//#endregion
//#region src/uploader/components/FileManager.tsx
function FileDropArea({ isDragInside, isDragOutside, canAddFiles, onAddFiles, onDelete, addFilesDescription, addMoreFilesDescription, acceptDescription, placeholderText, renderPlaceholder, fileGroups, renderFiles, renderFile }) {
	return fileGroups.length ? renderFiles ? renderFiles({
		isDragInside,
		isDragOutside,
		fileGroups,
		acceptDescription,
		canAddFiles,
		onAddFiles,
		addFilesDescription: addMoreFilesDescription,
		onDelete,
		renderFile
	}) : /* @__PURE__ */ React.createElement(FileItemGroups, {
		isDragInside,
		isDragOutside,
		fileGroups,
		acceptDescription,
		canAddFiles,
		onAddFiles,
		addFilesDescription: addMoreFilesDescription,
		onDelete,
		renderFile
	}) : renderPlaceholder ? renderPlaceholder({
		isDragInside,
		isDragOutside,
		placeholderText,
		canAddFiles,
		onAddFiles,
		addFilesDescription
	}) : /* @__PURE__ */ React.createElement(DropZonePlaceholder, {
		isDragInside,
		isDragOutside,
		placeholderText,
		canAddFiles,
		onAddFiles,
		addFilesDescription
	});
}
function FileManager({ rejectedFiles, pendingFiles, processingFiles, failedFiles, completedFiles, maxSize, maxFiles = Infinity, accept, placeholderText, renderPlaceholder, renderFiles, renderFile, onFileAccepted, onFileRejected, onDelete, className }) {
	const acceptDescription = useMemo(() => {
		const fileTypes = accept ? Object.values(accept).flat() : void 0;
		if (!fileTypes || !fileTypes.length) return;
		return fileTypes.join(", ").trim().toUpperCase();
	}, [accept]);
	const maxFilesText = useMemo(() => maxFiles > 1 && maxFiles !== Infinity ? ` ${maxFiles} ` : " ", [maxFiles]);
	const addFilesDescription = useMemo(() => {
		const filesInflection = getFilesInflection(maxFiles);
		return `Add${maxFilesText}${filesInflection}`;
	}, [maxFiles, maxFilesText]);
	const addMoreFilesDescription = useMemo(() => `Add${maxFilesText}more`, [maxFilesText]);
	const getFileGroups = () => {
		return [
			createRejectedFileUploadGroup(rejectedFiles),
			createPendingFileUploadGroup(pendingFiles),
			createProcessingFileUploadGroup(processingFiles),
			createFailedFileUploadGroup(failedFiles),
			createCompletedFileUploadGroup(completedFiles)
		].filter((fileGroup) => fileGroup.groupFiles.length > 0);
	};
	const fileGroups = getFileGroups();
	const canAddFiles = maxFiles > 0;
	return /* @__PURE__ */ React.createElement(DropZone, {
		onFileAccepted,
		onFileRejected,
		maxFiles,
		accept,
		maxSize,
		className,
		renderer: (dropZoneProps) => /* @__PURE__ */ React.createElement(FileDropArea, {
			...dropZoneProps,
			fileGroups,
			canAddFiles,
			placeholderText,
			renderPlaceholder,
			renderFiles,
			renderFile,
			addFilesDescription,
			addMoreFilesDescription,
			acceptDescription,
			onDelete
		})
	});
}
//#endregion
//#region src/uploader/components/LinearProgress.tsx
var LinearProgressContainer = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		position: "relative",
		height: "3px"
	},
	...props
});
var BackgroundLine = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		position: "absolute",
		top: "1px",
		left: "0px",
		width: "100%",
		border: "1px solid {colors.border.muted}"
	},
	...props
});
var ProgressLine = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		position: "absolute",
		top: "0px",
		left: "0px",
		border: "2px solid {colors.border.accent.brand.hero}",
		transition: "width 0.5s ease-in-out"
	},
	...props
});
function LinearProgress({ progress, className }) {
	return /* @__PURE__ */ React.createElement(LinearProgressContainer, { className }, /* @__PURE__ */ React.createElement(BackgroundLine, null), progress > 0 && /* @__PURE__ */ React.createElement(ProgressLine, { style: { width: `${progress}%` } }));
}
//#endregion
//#region src/uploader/components/FileWorkflowStep.tsx
var FileWorkflowStepType = /* @__PURE__ */ function(FileWorkflowStepType) {
	FileWorkflowStepType[FileWorkflowStepType["Add"] = 0] = "Add";
	FileWorkflowStepType[FileWorkflowStepType["Pending"] = 1] = "Pending";
	FileWorkflowStepType[FileWorkflowStepType["Processing"] = 2] = "Processing";
	FileWorkflowStepType[FileWorkflowStepType["Failed"] = 3] = "Failed";
	FileWorkflowStepType[FileWorkflowStepType["Complete"] = 4] = "Complete";
	return FileWorkflowStepType;
}({});
var containerMixin = {
	display: "grid",
	columnGap: "1rem",
	alignItems: "center",
	minHeight: "2.75rem"
};
var PendingContainer = ({ hasPrimaryAction: _p, hasSecondaryAction: _s, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		...containerMixin,
		gridTemplateAreas: `'. primary-action'`,
		gridTemplateColumns: "1fr auto",
		"@media (max-width: 500px)": {
			gridTemplateAreas: `'primary-action'`,
			gridTemplateColumns: "1fr"
		}
	},
	...props
});
var ProcessingContainer = ({ hasPrimaryAction: _p, hasSecondaryAction: _s, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		...containerMixin,
		gridTemplateAreas: `'description'`,
		gridTemplateColumns: "1fr",
		justifyItems: "start",
		"@media (max-width: 500px)": { justifyItems: "center" }
	},
	...props
});
var FailedContainer = ({ hasSecondaryAction, hasPrimaryAction: _p, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		...containerMixin,
		gridTemplateAreas: hasSecondaryAction ? `'description secondary-action primary-action'` : `'description primary-action'`,
		gridTemplateColumns: hasSecondaryAction ? "1fr auto auto" : "1fr auto",
		"@media (max-width: 500px)": {
			gridTemplateAreas: hasSecondaryAction ? `'. secondary-action primary-action'` : `'primary-action'`,
			gridTemplateColumns: hasSecondaryAction ? "1fr auto auto" : "1fr",
			"--_description-display": "none"
		}
	},
	...props
});
var CompleteContainer = ({ hasPrimaryAction, hasSecondaryAction: _s, ...props }) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		...containerMixin,
		gridTemplateAreas: hasPrimaryAction ? `'description primary-action'` : `'description'`,
		gridTemplateColumns: hasPrimaryAction ? "1fr auto" : "1fr",
		"@media (max-width: 500px)": {
			gridTemplateAreas: hasPrimaryAction ? `'primary-action'` : `'description'`,
			gridTemplateColumns: "1fr",
			justifyItems: "center",
			"--_description-display": hasPrimaryAction ? "none" : ""
		}
	},
	...props
});
var Description = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "var(--_description-display, flex)",
		gridArea: "description"
	},
	...props
});
var Indicator = (props) => /* @__PURE__ */ React.createElement(Box, {
	css: {
		display: "inline-block",
		marginRight: "0.25rem",
		alignSelf: "center"
	},
	...props
});
var SecondaryAction = (props) => /* @__PURE__ */ React.createElement(TextButton, {
	css: { gridArea: "secondary-action" },
	...props
});
var PrimaryAction = (props) => /* @__PURE__ */ React.createElement(Button, {
	css: { gridArea: "primary-action" },
	...props
});
function getContainer(stepType) {
	switch (stepType) {
		case 1: return PendingContainer;
		case 2: return ProcessingContainer;
		case 3: return FailedContainer;
		case 4: return CompleteContainer;
	}
}
function FileWorkflowStep({ stepType, description, primaryAction, secondaryAction, className }) {
	const hasPrimaryAction = !!primaryAction;
	const hasSecondaryAction = !!secondaryAction;
	const Container = getContainer(stepType);
	return Container ? /* @__PURE__ */ React.createElement(Container, {
		hasPrimaryAction,
		hasSecondaryAction,
		className
	}, description && /* @__PURE__ */ React.createElement(Description, null, /* @__PURE__ */ React.createElement(Indicator, null, stepType === 2 ? /* @__PURE__ */ React.createElement(Spinner, null) : stepType === 3 ? /* @__PURE__ */ React.createElement(Icon, {
		size: "md",
		css: { color: "{colors.icon.feedback.critical.hero}" }
	}, /* @__PURE__ */ React.createElement(IconWarningFilled, null)) : stepType === 4 ? /* @__PURE__ */ React.createElement(Icon, {
		size: "md",
		css: { color: "{colors.icon.feedback.success.hero}" }
	}, /* @__PURE__ */ React.createElement(IconCheckmarkFilled, null)) : null), /* @__PURE__ */ React.createElement(Text, { textStyle: "body-sm" }, description)), secondaryAction && /* @__PURE__ */ React.createElement(SecondaryAction, {
		type: "button",
		onClick: secondaryAction.action
	}, secondaryAction.description), primaryAction && /* @__PURE__ */ React.createElement(PrimaryAction, {
		fluid: true,
		type: "button",
		emphasis: "filled",
		onClick: primaryAction.action
	}, primaryAction.description)) : null;
}
//#endregion
//#region src/uploader/components/FileWorkflowManager.tsx
function getUploadProgress(processingFiles, failedCount, completedCount) {
	const finishedCount = failedCount + completedCount;
	const finishedProgress = finishedCount * 100;
	const fileCount = processingFiles.length + finishedCount;
	const uploadProgress = processingFiles.reduce((uploadProgress, fileUpload) => uploadProgress + fileUpload.progress, finishedProgress);
	return Math.floor(uploadProgress / fileCount) || 0;
}
function getDoneAction(onDone) {
	return {
		action: onDone,
		description: "Done"
	};
}
function getAddStep() {
	return { stepType: FileWorkflowStepType.Add };
}
function getPendingStep(fileCount, onUploadPending) {
	const fileInflection = getFilesInflection(fileCount);
	return {
		stepType: FileWorkflowStepType.Pending,
		primaryAction: {
			action: onUploadPending,
			description: `Upload ${fileCount} ${fileInflection}`
		}
	};
}
function getProcessingStep(uploadProgress) {
	return {
		stepType: FileWorkflowStepType.Processing,
		description: `Uploading: ${uploadProgress}% complete`
	};
}
function getFailedStep(failedCount, completedCount, onRetry, onDone) {
	const fileCount = failedCount + completedCount;
	const retryAction = {
		action: onRetry,
		description: `Retry ${failedCount} ${getFilesInflection(failedCount)}`
	};
	const primaryAction = completedCount > 0 && !!onDone ? getDoneAction(onDone) : retryAction;
	const secondaryAction = primaryAction !== retryAction ? retryAction : void 0;
	return {
		stepType: FileWorkflowStepType.Failed,
		description: `${failedCount}/${fileCount} Uploads failed`,
		primaryAction,
		secondaryAction
	};
}
function getCompleteStep(onDone) {
	return {
		stepType: FileWorkflowStepType.Complete,
		description: "Complete",
		primaryAction: onDone ? getDoneAction(onDone) : void 0
	};
}
var StyledLinearProgress = (props) => /* @__PURE__ */ React.createElement(Box, { css: { margin: "1rem 0" } }, /* @__PURE__ */ React.createElement(LinearProgress, props));
function FileWorkflowManager({ pendingFiles, processingFiles, failedFiles, completedFiles, onUploadPending, onRetry, onDone, renderWorkflow }) {
	const uploadProgress = getUploadProgress(processingFiles, failedFiles.length, completedFiles.length);
	function getWorkflowStep() {
		if (pendingFiles.length > 0) return getPendingStep(pendingFiles.length, onUploadPending);
		if (processingFiles.length > 0) return getProcessingStep(uploadProgress);
		if (failedFiles.length > 0) return getFailedStep(failedFiles.length, completedFiles.length, onRetry, onDone);
		if (completedFiles.length > 0) return getCompleteStep(onDone);
		return getAddStep();
	}
	const workflowStep = getWorkflowStep();
	return renderWorkflow ? renderWorkflow({
		...workflowStep,
		uploadProgress
	}) : workflowStep.stepType !== FileWorkflowStepType.Add ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledLinearProgress, { progress: uploadProgress }), /* @__PURE__ */ React.createElement(FileWorkflowStep, workflowStep)) : null;
}
//#endregion
//#region src/uploader/components/ZDocsUploader.tsx
function ZDocsUploader({ generateUploadToken, onDone, onUploadActive, onUploadComplete, onUploadFailed, onFileRejected, placeholderText, accept, maxSize = 1e8, baseUrl, maxRetries, maxFiles: totalMaxFiles = Infinity, autoUpload = true, renderer, renderPlaceholder, renderFiles, renderFile, renderWorkflow }) {
	const [rejectedFiles, setRejectedFiles] = useState([]);
	const [pendingFiles, setPendingFiles] = useState([]);
	const [processingFiles, setProcessingFiles] = useState([]);
	const [failedFiles, setFailedFiles] = useState([]);
	const [completedFiles, setCompletedFiles] = useState([]);
	function getMaxFiles() {
		if (totalMaxFiles === Infinity) return totalMaxFiles;
		return totalMaxFiles - (rejectedFiles.filter((fileUpload) => fileUpload.errorCode === RejectionErrorCode.ExceededMaxFiles).length + pendingFiles.length + processingFiles.length + failedFiles.length + completedFiles.length);
	}
	const maxFiles = getMaxFiles();
	const handleUploadProcessing = useCallback((_, allUploads, processingGroupId) => {
		const groupIsProcessing = allUploads.some((upload) => upload.processingGroupId === processingGroupId && (upload.status === FileUploadStatus.Pending || upload.status === FileUploadStatus.Uploading));
		const changes = allUploads.reduce((changes, upload) => {
			const { fileId, processingGroupId: groupId, status } = upload;
			if (groupId === processingGroupId && groupIsProcessing || status === FileUploadStatus.Pending || status === FileUploadStatus.Uploading) changes.processing.push(createProcessingFileUpload(fileId, upload));
			else if (status === FileUploadStatus.Completed) changes.completed.push(createCompletedFileUpload(fileId, upload));
			else if (status === FileUploadStatus.Rejected) changes.rejected.push(createServerRejectedFileUpload(fileId, upload));
			else changes.failed.push(createFailedFileUpload(fileId, upload));
			return changes;
		}, {
			processing: [],
			failed: [],
			completed: [],
			rejected: []
		});
		setProcessingFiles(changes.processing);
		setFailedFiles(changes.failed);
		setCompletedFiles(changes.completed);
		setRejectedFiles((rejectedFiles) => {
			const newRejectedFiles = changes.rejected.filter((nr) => !rejectedFiles.find((r) => r.fileId === nr.fileId));
			return rejectedFiles.concat(newRejectedFiles);
		});
	}, []);
	const uploadManagerRef = useRef(new ZDocsUploadManager({
		generateUploadToken,
		onChange: handleUploadProcessing,
		onUploadActive,
		onUploadComplete,
		onUploadFailed,
		baseUrl,
		maxRetries
	}));
	const handleFileAccepted = useCallback((files) => {
		const { current: uploadManager } = uploadManagerRef;
		if (autoUpload) {
			uploadManager.uploadFiles(files);
			return;
		}
		const fileUploads = files.map((file) => {
			return createPendingFileUpload(uploadManager.getFileId(file), file);
		});
		setPendingFiles((pendingFiles) => pendingFiles.concat(fileUploads));
	}, [autoUpload]);
	const handleFileRejected = useCallback((fileRejections) => {
		if (onFileRejected) {
			onFileRejected(fileRejections);
			return;
		}
		const { current: uploadManager } = uploadManagerRef;
		const { rejections } = fileRejections;
		const fileUploads = rejections.map((fileRejection) => {
			return createClientRejectedFileUpload(uploadManager.getFileId(fileRejection.file), fileRejection, maxSize);
		});
		setRejectedFiles((rejectedFiles) => rejectedFiles.concat(fileUploads));
	}, [onFileRejected, maxSize]);
	const handleRetry = useCallback(() => {
		setFailedFiles((failedFiles) => {
			if (failedFiles.length < 1) return failedFiles;
			const files = failedFiles.map((failedFile) => failedFile.file);
			uploadManagerRef.current.uploadFiles(files);
			return [];
		});
	}, []);
	const handleUploadPending = useCallback(() => {
		setPendingFiles((pendingFiles) => {
			if (pendingFiles.length < 1) return pendingFiles;
			const files = pendingFiles.map((pendingFile) => pendingFile.file);
			uploadManagerRef.current.uploadFiles(files);
			return [];
		});
	}, []);
	const handleDelete = useCallback((fileUpload) => {
		const { groupType, canDelete, file } = fileUpload;
		const exceededMaxFiles = RejectionErrorCode.ExceededMaxFiles;
		const deleteFileInManager = groupType !== FileUploadGroupType.Pending;
		let maxAdd = 0;
		if (!canDelete) return;
		switch (groupType) {
			case FileUploadGroupType.Rejected:
				if (fileUpload.errorCode === exceededMaxFiles && maxFiles + 1 === 0) maxAdd = totalMaxFiles;
				setRejectedFiles((rejectedFiles) => rejectedFiles.filter((rejectedFile) => rejectedFile !== fileUpload));
				break;
			case FileUploadGroupType.Pending:
				maxAdd = 1;
				setPendingFiles((pendingFiles) => pendingFiles.filter((pendingFile) => pendingFile !== fileUpload));
				break;
			case FileUploadGroupType.Failed:
				maxAdd = 1;
				setFailedFiles((failedFiles) => failedFiles.filter((failedFile) => failedFile !== fileUpload));
				break;
		}
		if (deleteFileInManager) uploadManagerRef.current.deleteFile(file);
		if (maxAdd > 0) setRejectedFiles((rejectedFiles) => {
			const availableFiles = rejectedFiles.reduce((availableFiles, rejectedFile) => {
				if (rejectedFile.errorCode === exceededMaxFiles && availableFiles.length < maxAdd) availableFiles.push(rejectedFile);
				return availableFiles;
			}, []);
			if (availableFiles.length) {
				rejectedFiles = rejectedFiles.filter((rejectedFile) => !availableFiles.includes(rejectedFile));
				setPendingFiles((pendingFiles) => pendingFiles.concat(availableFiles.map((availableFile) => toPendingFileUpload(availableFile))));
			}
			return rejectedFiles;
		});
	}, [maxFiles, totalMaxFiles]);
	useEffect(() => {
		const { current: uploadManager } = uploadManagerRef;
		uploadManager.generateUploadToken = generateUploadToken;
		uploadManager.onChange = handleUploadProcessing;
		uploadManager.onUploadActive = onUploadActive;
		uploadManager.onUploadComplete = onUploadComplete;
		uploadManager.onUploadFailed = onUploadFailed;
		return () => uploadManager.disconnect();
	}, [
		generateUploadToken,
		handleUploadProcessing,
		onUploadActive,
		onUploadComplete,
		onUploadFailed
	]);
	return renderer ? renderer({
		rejectedFiles,
		pendingFiles,
		processingFiles,
		failedFiles,
		completedFiles,
		maxSize,
		maxFiles,
		accept,
		placeholderText,
		onFileAccepted: handleFileAccepted,
		onFileRejected: handleFileRejected,
		onDelete: handleDelete,
		onUploadPending: handleUploadPending,
		onRetry: handleRetry,
		onDone,
		renderPlaceholder,
		renderFiles,
		renderFile,
		renderWorkflow
	}) : /* @__PURE__ */ React.createElement(Fragment, null, /* @__PURE__ */ React.createElement(FileManager, {
		rejectedFiles,
		pendingFiles,
		processingFiles,
		failedFiles,
		completedFiles,
		maxFiles,
		maxSize,
		accept,
		placeholderText,
		onFileAccepted: handleFileAccepted,
		onFileRejected: handleFileRejected,
		onDelete: handleDelete,
		renderPlaceholder,
		renderFiles,
		renderFile
	}), /* @__PURE__ */ React.createElement(FileWorkflowManager, {
		pendingFiles,
		processingFiles,
		failedFiles,
		completedFiles,
		onUploadPending: handleUploadPending,
		onRetry: handleRetry,
		onDone,
		renderWorkflow
	}));
}
//#endregion
export { DropZone, DropZoneOverlay, DropZonePlaceholder, FieldType, FileItem, FileItemGroups as FileItemGroup, FileManager, FilePreview, FileUploadGroupType, FileUploadStatus, FileWorkflowManager, FileWorkflowStep, FileWorkflowStepType, IFrameErrorTypes, IFrameModalEventName, LinearProgress, RejectionErrorCode, ZDocsEditor, ZDocsOrganizePages, ZDocsUploadManager, ZDocsUploader, errorMessageFromCode };

//# sourceMappingURL=index.mjs.map